import React, { useState, useEffect, useCallback } from 'react';
import { useLocalization } from '../../../../hooks/useLocalization';
import ImagePickerModal from '../../shared/ImagePickerModal';
import MultiLangEditor from '../../MultiLangEditor';
import type {
    DestinationContent,
    DestinationActivity,
    DestinationAttraction,
    DestinationRestaurant,
    DestinationItinerary,
    DestinationPracticalInfo,
    WeatherData
} from '../../../../types';

interface DestinationEditorProps {
    destination: DestinationContent;
    onSave: (destination: DestinationContent) => Promise<void>;
    onClose: () => void;
    theme?: 'light' | 'dark';
}

// Helper function to safely extract localized text from potentially localized objects
const getLocalizedText = (value: any, lang: 'en' | 'sv' | 'da' = 'en'): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        // Handle {en, sv, da} translation objects
        if (value[lang] !== undefined) return String(value[lang]);
        if (value.en !== undefined) return String(value.en);
        if (value.sv !== undefined) return String(value.sv);
        if (value.da !== undefined) return String(value.da);
        // Fallback: return first string value found
        for (const key of Object.keys(value)) {
            if (typeof value[key] === 'string') return value[key];
        }
    }
    return String(value);
};

const DestinationEditor: React.FC<DestinationEditorProps> = ({
    destination: initialDestination,
    onSave,
    onClose,
    theme = 'light'
}) => {
    const { language } = useLocalization();
    const isDark = theme === 'dark';
    const [destination, setDestination] = useState<DestinationContent>(initialDestination);
    const [activeTab, setActiveTab] = useState<string>('basic');
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // SerpAPI State
    const [serpApiImages, setSerpApiImages] = useState<any[]>([]);
    const [serpApiLoading, setSerpApiLoading] = useState(false);

    // Per-restaurant Pexels image search state
    const [restPexelsOpen, setRestPexelsOpen] = useState<number | null>(null);
    const [restPexelsQuery, setRestPexelsQuery] = useState<string>('');
    const [restPexelsResults, setRestPexelsResults] = useState<any[]>([]);
    const [restPexelsLoading, setRestPexelsLoading] = useState(false);
    const [restPexelsDownloading, setRestPexelsDownloading] = useState<string | null>(null);
    const [tripAdvisorData, setTripAdvisorData] = useState<{ attractions: any[], restaurants: any[] }>({ attractions: [], restaurants: [] });
    const [tripAdvisorLoading, setTripAdvisorLoading] = useState(false);

    // Image Upload State
    const [uploadingMain, setUploadingMain] = useState(false);
    const [uploadingHero, setUploadingHero] = useState(false);
    const [uploadingContent, setUploadingContent] = useState<string | null>(null);
    const [uploadingRichImage, setUploadingRichImage] = useState<string | null>(null);
    const contentFileInputRef = React.useRef<HTMLInputElement>(null);
    const [pendingContentUpload, setPendingContentUpload] = useState<{
        type: 'activity' | 'attraction' | 'restaurant' | 'itinerary';
        index: number;
    } | null>(null);

    // Pexels Stock Image Search State
    const [pexelsQuery, setPexelsQuery] = useState('');
    const [pexelsResults, setPexelsResults] = useState<any[]>([]);
    const [pexelsLoading, setPexelsLoading] = useState(false);
    const [pexelsPage, setPexelsPage] = useState(1);
    const [pexelsTotalResults, setPexelsTotalResults] = useState(0);
    const [pexelsDownloading, setPexelsDownloading] = useState<string | null>(null);

    // Image Picker Modal State
    const [imagePickerOpen, setImagePickerOpen] = useState(false);
    const [imagePickerTarget, setImagePickerTarget] = useState<'main' | 'hero' | { type: 'activity' | 'attraction' | 'itinerary'; index: number } | null>(null);

    // AI Enrichment State
    const [aiEnriching, setAiEnriching] = useState(false);
    const [aiSection, setAiSection] = useState<string>('');
    const [aiResult, setAiResult] = useState<any>(null);
    const [showAiPanel, setShowAiPanel] = useState(false);
    const [enrichAllProgress, setEnrichAllProgress] = useState<{ current: string; done: string[]; total: number } | null>(null);

    // Tour Search State
    const [tourSearchResults, setTourSearchResults] = useState<any[]>([]);
    const [tourSearchLoading, setTourSearchLoading] = useState(false);
    const [tourSearchId, setTourSearchId] = useState<string>('');
    const [selectedTourHotels, setSelectedTourHotels] = useState<Set<string>>(new Set());

    // CTA Bar State
    const [ctaEditing, setCtaEditing] = useState(false);
    const [ctaTextEn, setCtaTextEn] = useState('');
    const [ctaTextSv, setCtaTextSv] = useState('');

    // Multi-language State
    const [contentLang, setContentLang] = useState<'en' | 'sv' | 'da'>('en');
    const [translating, setTranslating] = useState(false);

    // DnD State for hotel whitelist
    const [dragIdx, setDragIdx] = useState<number | null>(null);

    // Upload image to server
    const uploadImage = async (file: File, type: 'main' | 'hero') => {
        const id = destination._id || destination.destinationId;
        if (!id) {
            setMessage({ type: 'error', text: 'Please save the destination first before uploading images' });
            return;
        }

        const setUploading = type === 'main' ? setUploadingMain : setUploadingHero;
        setUploading(true);
        setMessage(null);

        try {
            const formData = new FormData();
            formData.append('image', file);

            const res = await fetch(`/api/destinations/${id}/upload-image?type=${type}`, {
                method: 'POST',
                body: formData
            });

            const data = await res.json();

            if (data.success) {
                if (type === 'hero') {
                    updateLandingPage('heroImage', data.imageUrl);
                } else {
                    updateField('imageUrl', data.imageUrl);
                }
                setMessage({ type: 'success', text: `Image uploaded successfully!` });
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to upload image' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to upload image' });
        } finally {
            setUploading(false);
        }
    };

    // Trigger content image upload
    const triggerContentImageUpload = (type: 'activity' | 'attraction' | 'restaurant' | 'itinerary', index: number) => {
        setPendingContentUpload({ type, index });
        contentFileInputRef.current?.click();
    };

    // Handle content file selection
    const handleContentFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !pendingContentUpload) return;

        const { type, index } = pendingContentUpload;
        const uploadKey = `${type}-${index}`;
        setUploadingContent(uploadKey);
        setMessage(null);

        try {
            const id = destination._id || destination.destinationId;
            if (!id) {
                setMessage({ type: 'error', text: 'Please save the destination first' });
                return;
            }

            const formData = new FormData();
            formData.append('image', file);

            const res = await fetch(`/api/destinations/${id}/upload-image?type=gallery`, {
                method: 'POST',
                body: formData
            });

            const data = await res.json();

            if (data.success) {
                // Update the image field based on content type
                switch (type) {
                    case 'activity':
                        updateActivity(index, 'image', data.imageUrl);
                        break;
                    case 'attraction':
                        updateAttraction(index, 'image', data.imageUrl);
                        break;
                    case 'restaurant':
                        updateRestaurant(index, 'image', data.imageUrl);
                        break;
                    case 'itinerary':
                        updateItinerary(index, 'image', data.imageUrl);
                        break;
                }
                setMessage({ type: 'success', text: 'Image uploaded!' });
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to upload' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to upload image' });
        } finally {
            setUploadingContent(null);
            setPendingContentUpload(null);
            // Reset file input
            if (contentFileInputRef.current) {
                contentFileInputRef.current.value = '';
            }
        }
    };

    const tabs = [
        { id: 'basic', label: 'Basic Info', icon: 'info' },
        { id: 'airport', label: 'Airport Info', icon: 'flight' },
        { id: 'blog', label: 'Blog', icon: 'article' },
        { id: 'sections', label: 'Sections', icon: 'view_module' },
        { id: 'serpapi', label: 'SerpAPI Images', icon: 'image_search' },
        { id: 'tripadvisor', label: 'TripAdvisor', icon: 'travel_explore' },
        { id: 'activities', label: 'Things To Do', icon: 'local_activity' },
        { id: 'attractions', label: 'Attractions', icon: 'place' },
        { id: 'restaurants', label: 'Restaurants', icon: 'restaurant' },
        { id: 'itineraries', label: 'Itineraries', icon: 'route' },
        { id: 'practical', label: 'Practical Info', icon: 'help_outline' },
        { id: 'weather', label: 'Weather', icon: 'wb_sunny' },
        { id: 'searchConfig', label: 'Search Config', icon: 'tune' },
    ];

    // Blog State
    const [blogPosts, setBlogPosts] = useState<any[]>([]);
    const [blogLoading, setBlogLoading] = useState(false);
    const [blogCategory, setBlogCategory] = useState('general');
    const [blogCustomTopic, setBlogCustomTopic] = useState('');
    const [blogGenerating, setBlogGenerating] = useState(false);
    const [blogPreview, setBlogPreview] = useState<any>(null);
    const [blogSaving, setBlogSaving] = useState(false);
    const [blogMsg, setBlogMsg] = useState<{ type: string; text: string } | null>(null);
    const [editingBlog, setEditingBlog] = useState<any>(null);

    // Load blog posts
    const loadBlogPosts = useCallback(async () => {
        const id = destination._id || destination.destinationId;
        if (!id) return;
        setBlogLoading(true);
        try {
            const res = await fetch(`/api/destination-blog/${id}`);
            const data = await res.json();
            if (data.success) setBlogPosts(data.posts || []);
        } catch (err) {
            console.warn('[Blog] Failed to load posts');
        } finally {
            setBlogLoading(false);
        }
    }, [destination._id, destination.destinationId]);

    useEffect(() => {
        if (activeTab === 'blog') loadBlogPosts();
    }, [activeTab, loadBlogPosts]);

    const handleBlogGenerate = async () => {
        const id = destination._id || destination.destinationId;
        if (!id) return;
        setBlogGenerating(true);
        setBlogPreview(null);
        setBlogMsg(null);
        try {
            const res = await fetch(`/api/destination-blog/${id}/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ category: blogCategory, customTopic: blogCustomTopic })
            });
            const data = await res.json();
            if (data.success) setBlogPreview(data.preview);
            else setBlogMsg({ type: 'error', text: data.error || 'Generation failed' });
        } catch (err: any) {
            setBlogMsg({ type: 'error', text: err.message });
        } finally {
            setBlogGenerating(false);
        }
    };

    const handleBlogSave = async (status: 'draft' | 'published') => {
        const id = destination._id || destination.destinationId;
        if (!id || !blogPreview) return;
        setBlogSaving(true);
        try {
            const res = await fetch(`/api/destination-blog/${id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...blogPreview, status })
            });
            const data = await res.json();
            if (data.success) {
                setBlogMsg({ type: 'success', text: `Blog post saved as ${status}!` });
                setBlogPreview(null);
                loadBlogPosts();
            } else {
                setBlogMsg({ type: 'error', text: data.error });
            }
        } catch (err: any) {
            setBlogMsg({ type: 'error', text: err.message });
        } finally {
            setBlogSaving(false);
        }
    };

    const handleBlogDelete = async (postId: string) => {
        if (!confirm('Delete this blog post?')) return;
        try {
            await fetch(`/api/destination-blog/post/${postId}`, { method: 'DELETE' });
            loadBlogPosts();
        } catch (err) {
            console.error('[Blog] Delete failed');
        }
    };

    const handleBlogPublish = async (postId: string, newStatus: string) => {
        try {
            await fetch(`/api/destination-blog/post/${postId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus })
            });
            loadBlogPosts();
        } catch (err) {
            console.error('[Blog] Status update failed');
        }
    };

    const handleBlogUpdate = async () => {
        if (!editingBlog?._id) return;
        setBlogSaving(true);
        try {
            const res = await fetch(`/api/destination-blog/post/${editingBlog._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title: editingBlog.title,
                    excerpt: editingBlog.excerpt,
                    content: editingBlog.content,
                    coverImage: editingBlog.coverImage,
                    category: editingBlog.category,
                    tags: editingBlog.tags,
                    status: editingBlog.status
                })
            });
            const data = await res.json();
            if (data.success) {
                setBlogMsg({ type: 'success', text: 'Blog post updated!' });
                setEditingBlog(null);
                loadBlogPosts();
            } else {
                setBlogMsg({ type: 'error', text: data.error || 'Update failed' });
            }
        } catch (err: any) {
            setBlogMsg({ type: 'error', text: err.message });
        } finally {
            setBlogSaving(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            await onSave(destination);
            setMessage({ type: 'success', text: 'Destination saved successfully!' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to save destination' });
        } finally {
            setSaving(false);
        }
    };

    // Update basic fields
    const updateField = (field: keyof DestinationContent, value: any) => {
        setDestination(prev => ({ ...prev, [field]: value }));
    };

    // Update nested landing page
    const updateLandingPage = (field: string, value: any) => {
        setDestination(prev => ({
            ...prev,
            landingPage: { ...prev.landingPage, [field]: value }
        }));
    };

    // Update rich content
    const updateRichContent = (section: 'history' | 'nature', lang: 'en' | 'sv', value: string) => {
        setDestination(prev => ({
            ...prev,
            richContent: {
                ...prev.richContent,
                [section]: { ...prev.richContent?.[section], [lang]: value }
            }
        }));
    };

    // Update rich content image
    const updateRichContentImage = (section: 'history' | 'nature', imageUrl: string) => {
        setDestination(prev => ({
            ...prev,
            richContent: {
                ...prev.richContent,
                [section]: { ...prev.richContent?.[section], image: imageUrl }
            }
        }));
    };

    // Upload image for rich content section (history/nature)
    const uploadRichContentImage = async (file: File, section: 'history' | 'nature') => {
        const id = destination._id || destination.destinationId;
        if (!id) {
            setMessage({ type: 'error', text: 'Please save the destination first before uploading images' });
            return;
        }
        setUploadingRichImage(section);
        setMessage(null);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await fetch(`/api/destinations/${id}/upload-image?type=gallery`, {
                method: 'POST',
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                updateRichContentImage(section, data.imageUrl);
                setMessage({ type: 'success', text: `${section === 'history' ? 'History' : 'Nature'} image uploaded!` });
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to upload image' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to upload image' });
        } finally {
            setUploadingRichImage(null);
        }
    };

    // Sections management
    const updateSections = (sectionKey: string, value: any) => {
        setDestination(prev => ({
            ...prev,
            sections: { ...prev.sections, [sectionKey]: value }
        }));
    };

    // Add activity
    const addActivity = () => {
        const newActivity: DestinationActivity = {
            id: `activity-${Date.now()}`,
            title: { en: 'New Activity', sv: 'Ny Aktivitet' },
            description: { en: '', sv: '' },
            image: '',
            category: 'adventure',
            duration: '',
            price: ''
        };
        const current = destination.sections?.thingsToDo || [];
        updateSections('thingsToDo', [...current, newActivity]);
    };

    // Update activity
    const updateActivity = (index: number, field: string, value: any) => {
        const activities = [...(destination.sections?.thingsToDo || [])];
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            (activities[index] as any)[parent] = { ...(activities[index] as any)[parent], [child]: value };
        } else {
            (activities[index] as any)[field] = value;
        }
        updateSections('thingsToDo', activities);
    };

    // Remove activity
    const removeActivity = (index: number) => {
        const activities = [...(destination.sections?.thingsToDo || [])];
        activities.splice(index, 1);
        updateSections('thingsToDo', activities);
    };

    // Add attraction
    const addAttraction = () => {
        const newAttraction: DestinationAttraction = {
            id: `attr-${Date.now()}`,
            title: { en: 'New Attraction', sv: 'Ny Sevärdhet' },
            description: { en: '', sv: '' },
            image: '',
            category: 'landmark',
            address: '',
            entryFee: ''
        };
        const current = destination.sections?.attractions || [];
        updateSections('attractions', [...current, newAttraction]);
    };

    // Update attraction
    const updateAttraction = (index: number, field: string, value: any) => {
        const attractions = [...(destination.sections?.attractions || [])];
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            (attractions[index] as any)[parent] = { ...(attractions[index] as any)[parent], [child]: value };
        } else {
            (attractions[index] as any)[field] = value;
        }
        updateSections('attractions', attractions);
    };

    // Remove attraction
    const removeAttraction = (index: number) => {
        const attractions = [...(destination.sections?.attractions || [])];
        attractions.splice(index, 1);
        updateSections('attractions', attractions);
    };

    // Add restaurant
    const addRestaurant = () => {
        const newRestaurant: DestinationRestaurant = {
            id: `rest-${Date.now()}`,
            name: 'New Restaurant',
            description: { en: '', sv: '' },
            image: '',
            cuisine: '',
            priceRange: '$$'
        };
        const current = destination.sections?.restaurants || [];
        updateSections('restaurants', [...current, newRestaurant]);
    };

    // Update restaurant
    const updateRestaurant = (index: number, field: string, value: any) => {
        const restaurants = [...(destination.sections?.restaurants || [])];
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            (restaurants[index] as any)[parent] = { ...(restaurants[index] as any)[parent], [child]: value };
        } else {
            (restaurants[index] as any)[field] = value;
        }
        updateSections('restaurants', restaurants);
    };

    // Remove restaurant
    const removeRestaurant = (index: number) => {
        const restaurants = [...(destination.sections?.restaurants || [])];
        restaurants.splice(index, 1);
        updateSections('restaurants', restaurants);
    };

    // Add itinerary
    const addItinerary = () => {
        const newItin: DestinationItinerary = {
            id: `itin-${Date.now()}`,
            title: { en: 'New Itinerary', sv: 'Ny Resplan' },
            description: { en: '', sv: '' },
            image: '',
            days: 1,
            highlights: { en: [], sv: [], da: [] }
        };
        const current = destination.sections?.itineraries || [];
        updateSections('itineraries', [...current, newItin]);
    };

    // Update itinerary
    const updateItinerary = (index: number, field: string, value: any) => {
        const itineraries = [...(destination.sections?.itineraries || [])];
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            (itineraries[index] as any)[parent] = { ...(itineraries[index] as any)[parent], [child]: value };
        } else {
            (itineraries[index] as any)[field] = value;
        }
        updateSections('itineraries', itineraries);
    };

    // Remove itinerary
    const removeItinerary = (index: number) => {
        const itineraries = [...(destination.sections?.itineraries || [])];
        itineraries.splice(index, 1);
        updateSections('itineraries', itineraries);
    };

    // Weather update
    const updateWeather = (month: string, field: keyof WeatherData, value: any) => {
        setDestination(prev => ({
            ...prev,
            weather: {
                ...prev.weather,
                [month]: { ...prev.weather?.[month], [field]: value }
            }
        }));
    };

    // SerpAPI Functions
    const fetchSerpApiImages = async (refresh = false) => {
        if (!destination._id && !destination.destinationId) return;
        setSerpApiLoading(true);
        try {
            const id = destination._id || destination.destinationId;
            const res = await fetch(`/api/destinations/${id}/serp-images?refresh=${refresh}`);
            const data = await res.json();
            if (data.success) {
                setSerpApiImages(data.images || []);
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to fetch images' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to fetch SerpAPI images' });
        } finally {
            setSerpApiLoading(false);
        }
    };

    const approveImage = async (index: number, approved: boolean, usedAs?: string) => {
        if (!destination._id && !destination.destinationId) return;
        try {
            const id = destination._id || destination.destinationId;
            const res = await fetch(`/api/destinations/${id}/approve-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ imageIndex: index, approved, usedAs })
            });
            const data = await res.json();
            if (data.success) {
                const updated = [...serpApiImages];
                updated[index] = { ...updated[index], approved, usedAs };
                setSerpApiImages(updated);
                setMessage({ type: 'success', text: `Image ${approved ? 'approved' : 'rejected'}` });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to update image approval' });
        }
    };

    // Pexels Stock Image Functions
    const searchPexels = async (page = 1) => {
        if (!pexelsQuery.trim()) return;
        setPexelsLoading(true);
        try {
            const res = await fetch(`/api/media/stock/search?query=${encodeURIComponent(pexelsQuery)}&source=pexels&page=${page}&perPage=12&orientation=landscape`);
            const data = await res.json();
            if (data.success && data.data) {
                setPexelsResults(data.data.images || []);
                setPexelsTotalResults(data.data.totalResults || 0);
                setPexelsPage(page);
            } else {
                setMessage({ type: 'error', text: data.error || 'Pexels search failed' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to search Pexels' });
        } finally {
            setPexelsLoading(false);
        }
    };

    const downloadPexelsImage = async (image: any, useAs: 'main' | 'hero') => {
        setPexelsDownloading(`${image.id}-${useAs}`);
        try {
            // Download image to server via stock download API
            const res = await fetch('/api/media/stock/download', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    downloadUrl: image.downloadUrl || image.url,
                    source: 'pexels',
                    id: image.id,
                    alt: image.alt || pexelsQuery,
                    credit: image.photographer
                })
            });
            const data = await res.json();
            if (data.success && data.localUrl) {
                if (useAs === 'main') {
                    updateField('imageUrl', data.localUrl);
                } else {
                    updateLandingPage('heroImage', data.localUrl);
                }
                setMessage({ type: 'success', text: `Image saved as ${useAs === 'main' ? 'Main Image' : 'Hero Image'}!` });
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to download image' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to download image from Pexels' });
        } finally {
            setPexelsDownloading(null);
        }
    };

    // TripAdvisor Functions
    const fetchTripAdvisorData = async (refresh = false) => {
        if (!destination._id && !destination.destinationId) return;
        setTripAdvisorLoading(true);
        try {
            const id = destination._id || destination.destinationId;
            const res = await fetch(`/api/destinations/${id}/tripadvisor-poi?refresh=${refresh}`);
            const data = await res.json();
            if (data.success) {
                setTripAdvisorData({
                    attractions: data.attractions || [],
                    restaurants: data.restaurants || []
                });
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to fetch TripAdvisor data' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to fetch TripAdvisor data' });
        } finally {
            setTripAdvisorLoading(false);
        }
    };

    const approvePOI = async (type: 'attraction' | 'restaurant', index: number, approved: boolean) => {
        if (!destination._id && !destination.destinationId) return;
        try {
            const id = destination._id || destination.destinationId;
            const res = await fetch(`/api/destinations/${id}/approve-poi`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type, index, approved })
            });
            const data = await res.json();
            if (data.success) {
                if (type === 'attraction') {
                    const updated = [...tripAdvisorData.attractions];
                    updated[index] = { ...updated[index], approved };
                    setTripAdvisorData(prev => ({ ...prev, attractions: updated }));
                } else {
                    const updated = [...tripAdvisorData.restaurants];
                    updated[index] = { ...updated[index], approved };
                    setTripAdvisorData(prev => ({ ...prev, restaurants: updated }));
                }
                setMessage({ type: 'success', text: `${type === 'attraction' ? 'Attraction' : 'Restaurant'} ${approved ? 'approved' : 'rejected'}` });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to update POI approval' });
        }
    };

    // AI Content Enrichment
    const handleAiEnrich = async (section: string) => {
        const id = destination._id || destination.destinationId;
        if (!id) {
            setMessage({ type: 'error', text: 'Save the destination first before enriching' });
            return;
        }
        setAiEnriching(true);
        setAiSection(section);
        setAiResult(null);
        setShowAiPanel(true);
        try {
            const res = await fetch(`/api/destinations/${id}/ai-enrich`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ section, language, scrapeWikipedia: true })
            });
            const data = await res.json();
            if (data.success) {
                setAiResult(data);
                setMessage({ type: 'success', text: `AI content generated for ${section}` });
            } else {
                setMessage({ type: 'error', text: data.error || 'AI enrichment failed' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to enrich content' });
        } finally {
            setAiEnriching(false);
        }
    };

    // Enrich ALL sections at once
    const handleEnrichAll = async () => {
        const id = destination._id || destination.destinationId;
        if (!id) {
            setMessage({ type: 'error', text: 'Save the destination first before enriching' });
            return;
        }
        setAiEnriching(true);
        setShowAiPanel(true);
        setAiResult(null);
        const allSections = ['overview', 'history', 'nature', 'attractions', 'thingsToDo', 'itineraries', 'practicalInfo', 'weather', 'food'];
        setEnrichAllProgress({ current: 'Starting...', done: [], total: allSections.length });

        try {
            // Enrich each section sequentially and apply immediately
            for (const section of allSections) {
                setEnrichAllProgress(prev => prev ? { ...prev, current: section } : null);
                setAiSection(section);
                try {
                    const res = await fetch(`/api/destinations/${id}/ai-enrich`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ section, language, scrapeWikipedia: true })
                    });
                    const data = await res.json();
                    if (data.success) {
                        applyAiContentToFields(section, data.content);
                    }
                } catch (err) {
                    console.warn(`[AI Enrich] Failed for section ${section}:`, err);
                }
                setEnrichAllProgress(prev => prev ? { ...prev, done: [...prev.done, section] } : null);
            }

            setMessage({ type: 'success', text: 'All sections enriched! Auto-saving...' });

            // Auto-save after enriching all — use setDestination callback to get latest accumulated state
            setSaving(true);
            try {
                await new Promise<void>((resolve, reject) => {
                    setTimeout(() => {
                        setDestination(prev => {
                            onSave(prev).then(() => resolve()).catch(reject);
                            return prev;
                        });
                    }, 300);
                });
                setMessage({ type: 'success', text: 'All sections enriched and saved!' });
            } catch (saveErr) {
                setMessage({ type: 'error', text: 'Content enriched but save failed. Click Save manually.' });
            } finally {
                setSaving(false);
            }
        } finally {
            setAiEnriching(false);
            setEnrichAllProgress(null);
        }
    };

    // Tour Search — execute search from Search Config
    const handleTourSearch = async () => {
        const id = destination._id || destination.destinationId;
        if (!id) { setMessage({ type: 'error', text: 'Save destination first' }); return; }
        setTourSearchLoading(true);
        setTourSearchResults([]);
        setSelectedTourHotels(new Set());
        try {
            const tsc = (destination as any).tourSearchConfig || {};
            const futureDays = tsc.futureDays ?? 60;
            const checkIn = tsc.checkIn || new Date(Date.now() + futureDays * 86400000).toISOString().split('T')[0];
            // Use search-quick endpoint which handles priceSearch + getPagingData in one call
            const body = {
                arrivalId: (destination as any).tourvisioId || destination.destinationId || id,
                departureId: tsc.departureId || '10',
                checkIn,
                nights: tsc.nights ?? 7,
                adults: tsc.adults ?? 2,
                children: 0,
                childrenAges: [],
                currency: tsc.currency || 'SEK',
                culture: 'sv-SE',
                page: 1,
                pageSize: 50
            };
            const res = await fetch('/api/tourvisio/search-quick', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();
            if (data.success && data.hotels) {
                setTourSearchId(data.searchId || '');
                const hotels = data.hotels || [];
                setTourSearchResults(hotels);
                if (hotels.length === 0) setMessage({ type: 'error', text: 'No hotels found for these parameters' });
                else setMessage({ type: 'success', text: `Found ${hotels.length} hotels` });
            } else {
                setMessage({ type: 'error', text: data.error || 'Search failed' });
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message || 'Tour search failed' });
        } finally {
            setTourSearchLoading(false);
        }
    };

    // Add selected hotels to whitelist
    const addSelectedHotelsToWhitelist = () => {
        const current = (destination as any).hotelWhitelist || [];
        const currentIds = new Set(current.map((h: any) => h.hotelId));
        const newHotels = tourSearchResults
            .filter(h => selectedTourHotels.has(String(h.productId || h.id)))
            .filter(h => !currentIds.has(String(h.productId || h.id)))
            .map(h => ({
                hotelId: String(h.productId || h.id),
                name: h.hotelName || h.name || h.productName || `Hotel ${h.productId || h.id}`,
                imageUrl: h.thumbnailUrl || h.imageUrl || ''
            }));
        setDestination(prev => ({ ...prev, hotelWhitelist: [...current, ...newHotels] } as any));
        setSelectedTourHotels(new Set());
        setMessage({ type: 'success', text: `Added ${newHotels.length} hotels to whitelist` });
    };

    // Toggle CTA for a whitelist hotel
    const setHotelAsCta = (hotel: any) => {
        setCtaTextEn((destination as any).ctaBar?.text?.en || '');
        setCtaTextSv((destination as any).ctaBar?.text?.sv || '');
        setCtaEditing(true);
        setDestination(prev => ({
            ...prev,
            ctaBar: {
                ...(prev as any).ctaBar,
                enabled: true,
                hotelId: hotel.hotelId,
                hotelName: hotel.name,
                link: `/hotel/${hotel.hotelId}`
            }
        } as any));
    };

    const saveCtaText = () => {
        setDestination(prev => ({
            ...prev,
            ctaBar: {
                ...(prev as any).ctaBar,
                text: { en: ctaTextEn, sv: ctaTextSv }
            }
        } as any));
        setCtaEditing(false);
        setMessage({ type: 'success', text: 'CTA bar configured! Save to apply.' });
    };

    const removeCta = () => {
        setDestination(prev => ({
            ...prev,
            ctaBar: { enabled: false, hotelId: '', hotelName: '', text: { en: '', sv: '' }, link: '' }
        } as any));
    };

    // DnD handlers for hotel whitelist
    const handleDragStart = (idx: number) => setDragIdx(idx);
    const handleDragOver = (e: React.DragEvent) => e.preventDefault();
    const handleDrop = (targetIdx: number) => {
        if (dragIdx === null || dragIdx === targetIdx) return;
        const list = [...((destination as any).hotelWhitelist || [])];
        const [moved] = list.splice(dragIdx, 1);
        list.splice(targetIdx, 0, moved);
        setDestination(prev => ({ ...prev, hotelWhitelist: list } as any));
        setDragIdx(null);
    };

    // Translate content using AI
    const handleTranslate = async (targetLang: 'en' | 'sv' | 'da') => {
        const id = destination._id || destination.destinationId;
        if (!id) return;
        setTranslating(true);
        try {
            const sourceLang = contentLang;
            const fields: any = {};
            // Collect non-empty fields from source lang
            const desc = (destination.landingPage?.description as any)?.[sourceLang];
            if (desc) fields.description = desc;
            const hist = destination.richContent?.history?.[sourceLang as 'en' | 'sv'];
            if (hist) fields.history = hist;
            const nat = destination.richContent?.nature?.[sourceLang as 'en' | 'sv'];
            if (nat) fields.nature = nat;
            if (sourceLang === 'en' && getLocalizedText(destination.title, 'en')) fields.title = getLocalizedText(destination.title, 'en');

            if (Object.keys(fields).length === 0) {
                setMessage({ type: 'error', text: `No content in ${sourceLang.toUpperCase()} to translate` });
                return;
            }

            const res = await fetch(`/api/destinations/${id}/translate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sourceLang, targetLang, fields })
            });
            const data = await res.json();
            if (data.success && data.translated) {
                setDestination(prev => {
                    const updated = { ...prev };
                    if (data.translated.description) {
                        updated.landingPage = {
                            ...updated.landingPage,
                            description: { ...updated.landingPage?.description, [targetLang]: data.translated.description } as any
                        };
                    }
                    if (data.translated.history) {
                        updated.richContent = {
                            ...updated.richContent,
                            history: { ...updated.richContent?.history, [targetLang]: data.translated.history } as any
                        };
                    }
                    if (data.translated.nature) {
                        updated.richContent = {
                            ...updated.richContent,
                            nature: { ...updated.richContent?.nature, [targetLang]: data.translated.nature } as any
                        };
                    }
                    if (data.translated.title) {
                        if (targetLang === 'sv') (updated as any).titleSv = data.translated.title;
                        if (targetLang === 'da') (updated as any).titleDa = data.translated.title;
                    }
                    return updated;
                });
                setContentLang(targetLang);
                setMessage({ type: 'success', text: `Content translated to ${targetLang.toUpperCase()}` });
            } else {
                setMessage({ type: 'error', text: data.error || 'Translation failed' });
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message || 'Translation failed' });
        } finally {
            setTranslating(false);
        }
    };

    /**
     * Maps AI-generated content to the correct destination form fields
     */
    const applyAiContentToFields = (section: string, content: any) => {
        setDestination(prev => {
            const updated = { ...prev };

            switch (section) {
                case 'overview': {
                    // content = { en: "...", sv: "..." }
                    const desc = typeof content === 'object' && content.en
                        ? content
                        : { en: String(content), sv: '' };
                    updated.landingPage = {
                        ...updated.landingPage,
                        description: {
                            en: desc.en || updated.landingPage?.description?.en || '',
                            sv: desc.sv || updated.landingPage?.description?.sv || ''
                        }
                    };
                    break;
                }
                case 'history': {
                    const text = typeof content === 'object' && content.en
                        ? content
                        : { en: String(content), sv: '' };
                    updated.richContent = {
                        ...updated.richContent,
                        history: {
                            en: text.en || updated.richContent?.history?.en || '',
                            sv: text.sv || updated.richContent?.history?.sv || ''
                        },
                        nature: updated.richContent?.nature || { en: '', sv: '' }
                    };
                    break;
                }
                case 'nature': {
                    const text = typeof content === 'object' && content.en
                        ? content
                        : { en: String(content), sv: '' };
                    updated.richContent = {
                        ...updated.richContent,
                        nature: {
                            en: text.en || updated.richContent?.nature?.en || '',
                            sv: text.sv || updated.richContent?.nature?.sv || ''
                        },
                        history: updated.richContent?.history || { en: '', sv: '' }
                    };
                    break;
                }
                case 'food': {
                    const text = typeof content === 'object' && content.en
                        ? content
                        : { en: String(content), sv: '' };
                    updated.sections = {
                        ...updated.sections,
                        food: text as any
                    };
                    break;
                }
                case 'attractions': {
                    // content = DestinationAttraction[]
                    const items = Array.isArray(content) ? content : [];
                    updated.sections = {
                        ...updated.sections,
                        attractions: items.map((item: any, i: number) => ({
                            id: item.id || `attr_${i + 1}`,
                            title: typeof item.title === 'object' ? item.title : { en: item.title || item.name || '', sv: '' },
                            description: typeof item.description === 'object' ? item.description : { en: item.description || '', sv: '' },
                            image: item.image || '',
                            category: item.category || 'landmark',
                            address: item.address || '',
                            openingHours: item.openingHours || '',
                            entryFee: item.entryFee || ''
                        }))
                    };
                    break;
                }
                case 'thingsToDo': {
                    // content = DestinationActivity[]
                    const items = Array.isArray(content) ? content : [];
                    updated.sections = {
                        ...updated.sections,
                        thingsToDo: items.map((item: any, i: number) => ({
                            id: item.id || `act_${i + 1}`,
                            title: typeof item.title === 'object' ? item.title : { en: item.title || item.name || '', sv: '' },
                            description: typeof item.description === 'object' ? item.description : { en: item.description || '', sv: '' },
                            image: item.image || '',
                            category: item.category || 'culture',
                            duration: item.duration || '',
                            price: item.price || item.costRange || '',
                            rating: item.rating || undefined
                        }))
                    };
                    break;
                }
                case 'itineraries': {
                    // content = DestinationItinerary[]
                    const items = Array.isArray(content) ? content : [];
                    updated.sections = {
                        ...updated.sections,
                        itineraries: items.map((item: any, i: number) => ({
                            id: item.id || `itin_${i + 1}`,
                            title: typeof item.title === 'object' ? item.title : { en: item.title || '', sv: '' },
                            description: typeof item.description === 'object' ? item.description : { en: item.description || '', sv: '' },
                            image: item.image || '',
                            days: item.days || 3,
                            highlights: typeof item.highlights === 'object' && item.highlights?.en
                                ? { en: item.highlights.en || [], sv: item.highlights.sv || [], da: item.highlights.da || [] }
                                : { en: Array.isArray(item.highlights) ? item.highlights : [], sv: [], da: [] }
                        }))
                    };
                    break;
                }
                case 'practicalInfo': {
                    // content = DestinationPracticalInfo
                    if (content && typeof content === 'object') {
                        updated.sections = {
                            ...updated.sections,
                            practicalInfo: {
                                currency: content.currency || { code: '', name: { en: '', sv: '' } },
                                language: typeof content.language === 'object' ? content.language : { en: content.language || '', sv: '' },
                                timezone: content.timezone || '',
                                visaInfo: typeof content.visaInfo === 'object' ? content.visaInfo : { en: content.visaInfo || '', sv: '' },
                                bestTimeToVisit: typeof content.bestTimeToVisit === 'object' ? content.bestTimeToVisit : { en: content.bestTimeToVisit || '', sv: '' },
                                transport: Array.isArray(content.transport) ? content.transport : [],
                                tips: typeof content.tips === 'object' && content.tips?.en
                                    ? content.tips
                                    : { en: Array.isArray(content.tips) ? content.tips : [], sv: [] }
                            }
                        };
                    }
                    break;
                }
                case 'weather': {
                    // content = { jan: { temp, condition, water }, feb: {...}, ... }
                    if (content && typeof content === 'object' && !Array.isArray(content)) {
                        updated.weather = content;
                    } else if (Array.isArray(content)) {
                        // Convert array format to object format
                        const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
                        const weatherObj: any = {};
                        content.forEach((item: any, i: number) => {
                            const key = monthKeys[i] || item.month?.substring(0, 3).toLowerCase();
                            if (key) {
                                weatherObj[key] = {
                                    temp: item.temp || item.high || 0,
                                    condition: item.condition || item.description || '',
                                    water: item.water || 0
                                };
                            }
                        });
                        updated.weather = weatherObj;
                    }
                    break;
                }
            }

            // Mark as AI-enriched
            updated.dataSource = 'ai_enriched';
            return updated;
        });
    };

    // Apply single section content and auto-save
    const applyAiContent = async (section: string, content: any) => {
        applyAiContentToFields(section, content);
        setMessage({ type: 'success', text: `Applied AI content to ${section}. Auto-saving...` });
        setShowAiPanel(false);

        // Auto-save after a delay to ensure React has flushed the state update from applyAiContentToFields
        setTimeout(async () => {
            setSaving(true);
            try {
                // Use setDestination callback to reliably access the latest state
                setDestination(prev => {
                    onSave(prev).then(() => {
                        setMessage({ type: 'success', text: `Applied AI content to ${section} and saved!` });
                    }).catch(() => {
                        setMessage({ type: 'error', text: `Applied but save failed. Click Save manually.` });
                    }).finally(() => {
                        setSaving(false);
                    });
                    return prev;
                });
            } catch (err) {
                setSaving(false);
            }
        }, 300);
    };

    const inputClass = `w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-200 text-slate-900'} focus:ring-2 focus:ring-primary/50 outline-none`;
    const textareaClass = `${inputClass} resize-none`;
    const labelClass = `block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`;
    const cardClass = `p-4 rounded-xl ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'} mb-4`;

    return (
        <div className={`fixed inset-0 z-[9999] ${isDark ? 'bg-slate-900' : 'bg-slate-100'}`}>
            {/* Hidden file input for content image uploads */}
            <input
                type="file"
                ref={contentFileInputRef}
                onChange={handleContentFileSelect}
                accept="image/*"
                className="hidden"
            />
            {/* Header */}
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
                <div className="flex items-center gap-4">
                    <button onClick={onClose} className={`p-2 rounded-lg ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}>
                        <span className="material-icons">arrow_back</span>
                    </button>
                    <div>
                        <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {language === 'sv' ? 'Redigera Destination' : 'Edit Destination'}
                        </h1>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{getLocalizedText(destination.title, language)}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {message && (
                        <span className={`text-sm px-3 py-1 rounded-full ${message.type === 'success' ? (isDark ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-green-100 text-green-700') : (isDark ? 'bg-red-500/20 text-red-300 border border-red-500/30' : 'bg-red-100 text-red-700')}`}>
                            {message.text}
                        </span>
                    )}
                    <button
                        onClick={() => setShowAiPanel(!showAiPanel)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${showAiPanel ? 'bg-purple-600 text-white' : isDark ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/30' : 'bg-purple-50 text-purple-600 hover:bg-purple-100'}`}
                    >
                        <span>✨</span>
                        AI Enrich
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50"
                    >
                        <span className="material-icons text-sm">{saving ? 'hourglass_empty' : 'save'}</span>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className={`flex gap-2 px-6 py-3 border-b overflow-x-auto ${isDark ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-white'}`}>
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${activeTab === tab.id
                            ? 'bg-primary text-white'
                            : isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'
                            }`}
                    >
                        <span className="material-icons text-sm">{tab.icon}</span>
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* AI Enrichment Panel */}
            {showAiPanel && (
                <div className={`px-6 py-4 border-b ${isDark ? 'border-slate-700 bg-gradient-to-r from-purple-900/20 to-slate-800' : 'border-purple-200 bg-gradient-to-r from-purple-50 to-white'}`}>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className={`font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            <span>✨</span> AI Content Enrichment
                            {aiEnriching && <span className="animate-pulse text-purple-400 text-sm">(Generating...)</span>}
                        </h3>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleEnrichAll}
                                disabled={aiEnriching}
                                className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${aiEnriching
                                    ? 'bg-purple-600/50 text-purple-200 cursor-wait'
                                    : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500 hover:to-pink-500 shadow-lg shadow-purple-500/25'
                                    }`}
                            >
                                🚀 Enrich All Sections
                            </button>
                            <button onClick={() => setShowAiPanel(false)} className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>✕</button>
                        </div>
                    </div>
                    {/* Enrich All Progress */}
                    {enrichAllProgress && (
                        <div className={`mb-3 p-3 rounded-lg ${isDark ? 'bg-slate-800 border border-purple-700/30' : 'bg-purple-50 border border-purple-200'}`}>
                            <div className="flex items-center justify-between mb-2">
                                <span className={`text-xs font-medium ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                                    Enriching: <span className="capitalize font-bold">{enrichAllProgress.current.replace(/([A-Z])/g, ' $1').trim()}</span>
                                </span>
                                <span className={`text-xs ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                                    {enrichAllProgress.done.length}/{enrichAllProgress.total}
                                </span>
                            </div>
                            <div className={`w-full rounded-full h-2 ${isDark ? 'bg-slate-700' : 'bg-purple-100'}`}>
                                <div
                                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${(enrichAllProgress.done.length / enrichAllProgress.total) * 100}%` }}
                                />
                            </div>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2 mb-3">
                        {['overview', 'history', 'nature', 'attractions', 'thingsToDo', 'itineraries', 'practicalInfo', 'weather', 'food'].map(section => (
                            <button
                                key={section}
                                onClick={() => handleAiEnrich(section)}
                                disabled={aiEnriching}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all flex items-center gap-1 ${enrichAllProgress?.done.includes(section)
                                    ? 'bg-green-600/20 text-green-400 border border-green-600/30'
                                    : aiSection === section && aiEnriching
                                        ? 'bg-purple-600 text-white animate-pulse'
                                        : isDark ? 'bg-slate-700 text-slate-300 hover:bg-purple-600/30 hover:text-purple-300 border border-slate-600' : 'bg-white text-slate-600 hover:bg-purple-50 hover:text-purple-700 border border-slate-200'
                                    }`}
                            >
                                {enrichAllProgress?.done.includes(section) ? '✓ ' : ''}
                                {section.replace(/([A-Z])/g, ' $1').trim()}
                            </button>
                        ))}
                    </div>
                    {aiResult && (
                        <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                            <div className="flex items-center justify-between mb-2">
                                <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    Generated: <span className="capitalize">{aiResult.section?.replace(/([A-Z])/g, ' $1').trim()}</span>
                                </span>
                                <button
                                    onClick={() => applyAiContent(aiResult.section, aiResult.content)}
                                    className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-500 flex items-center gap-1"
                                >
                                    ✓ Apply & Save
                                </button>
                            </div>
                            {aiResult.wikiSource && (
                                <p className={`text-xs mb-2 ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>
                                    📖 Source: <a href={aiResult.wikiSource.url} target="_blank" rel="noopener noreferrer" className="underline">{aiResult.wikiSource.title}</a>
                                </p>
                            )}
                            <div className={`text-sm max-h-[200px] overflow-y-auto whitespace-pre-wrap ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                                {typeof aiResult.content === 'string'
                                    ? aiResult.content
                                    : typeof aiResult.content === 'object' && aiResult.content?.en
                                        ? `🇬🇧 EN:\n${aiResult.content.en}\n\n🇸🇪 SV:\n${aiResult.content.sv || '(not generated)'}`
                                        : JSON.stringify(aiResult.content, null, 2)
                                }
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Content */}
            <div className="overflow-y-auto p-6" style={{ height: 'calc(100vh - 140px)' }}>
                {/* Basic Info Tab */}
                {activeTab === 'basic' && (
                    <div className="max-w-4xl mx-auto space-y-6">
                        {/* Inactive Destination Warning */}
                        {!destination.isActive && (
                            <div className={`flex items-center gap-3 p-4 rounded-xl border ${isDark ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                                <span className="material-icons text-2xl">warning</span>
                                <div>
                                    <p className="font-bold">This destination is inactive</p>
                                    <p className="text-sm opacity-80">It won't appear on the public website. Check the "Active" box below and save to make it live.</p>
                                </div>
                            </div>
                        )}
                        <div className={cardClass}>
                            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Destination Details</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Title (English)</label>
                                    <input type="text" value={getLocalizedText(destination.title, 'en')} onChange={e => updateField('title', e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Title (Swedish)</label>
                                    <input type="text" value={destination.titleSv || ''} onChange={e => updateField('titleSv', e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Title (Danish)</label>
                                    <input type="text" value={(destination as any).titleDa || ''} onChange={e => updateField('titleDa', e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Destination ID</label>
                                    <input type="text" value={destination.destinationId} onChange={e => updateField('destinationId', e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Type</label>
                                    <input type="text" value={destination.type} onChange={e => updateField('type', e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Price</label>
                                    <input type="text" value={destination.price} onChange={e => updateField('price', e.target.value)} className={inputClass} />
                                </div>
                                <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2">
                                        <input type="checkbox" checked={destination.isActive} onChange={e => updateField('isActive', e.target.checked)} className="w-4 h-4" />
                                        <span className={labelClass}>Active</span>
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input type="checkbox" checked={destination.isFeatured || false} onChange={e => updateField('isFeatured', e.target.checked)} className="w-4 h-4" />
                                        <span className={labelClass}>Featured</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Language Selector & Translation */}
                        <div className={cardClass}>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    <span className="material-icons text-blue-500">translate</span>
                                    Content Language
                                </h3>
                                <div className="flex items-center gap-2">
                                    {(['en', 'sv', 'da'] as const).map(lang => (
                                        <button key={lang} onClick={() => setContentLang(lang)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${contentLang === lang
                                                ? 'bg-blue-600 text-white shadow-lg'
                                                : isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                                            {lang === 'en' ? '🇬🇧 EN' : lang === 'sv' ? '🇸🇪 SV' : '🇩🇰 DA'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Translate from {contentLang.toUpperCase()} to:</span>
                                {(['en', 'sv', 'da'] as const).filter(l => l !== contentLang).map(lang => (
                                    <button key={lang} onClick={() => handleTranslate(lang)} disabled={translating}
                                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 ${isDark ? 'bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 border border-indigo-500/30' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}>
                                        <span className="material-icons text-sm">{translating ? 'hourglass_empty' : 'auto_awesome'}</span>
                                        {lang === 'en' ? '🇬🇧 EN' : lang === 'sv' ? '🇸🇪 SV' : '🇩🇰 DA'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className={cardClass}>
                            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Images</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Main Image</label>
                                    <div className="space-y-2">
                                        <div className="flex gap-2">
                                            <input type="text" value={destination.imageUrl} onChange={e => updateField('imageUrl', e.target.value)} className={`${inputClass} flex-1`} placeholder="Enter URL or browse" />
                                            <button onClick={() => { setImagePickerTarget('main'); setImagePickerOpen(true); }} className="flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"><span className="material-icons text-sm">folder_open</span>Browse</button>
                                        </div>
                                        <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${isDark ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30' : 'bg-primary/10 text-primary hover:bg-primary/20'} ${uploadingMain ? 'opacity-50 pointer-events-none' : ''}`}>
                                            <span className="material-icons text-sm">{uploadingMain ? 'hourglass_empty' : 'upload'}</span>
                                            <span className="text-sm font-medium">{uploadingMain ? 'Uploading...' : 'Upload Image'}</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                disabled={uploadingMain}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) uploadImage(file, 'main');
                                                    e.target.value = '';
                                                }}
                                            />
                                        </label>
                                    </div>
                                    {destination.imageUrl && <img src={destination.imageUrl} alt="Preview" className="mt-2 h-32 object-cover rounded-lg" />}
                                </div>
                                <div>
                                    <label className={labelClass}>Hero Image</label>
                                    <div className="space-y-2">
                                        <div className="flex gap-2">
                                            <input type="text" value={destination.landingPage?.heroImage || ''} onChange={e => updateLandingPage('heroImage', e.target.value)} className={`${inputClass} flex-1`} placeholder="Enter URL or browse" />
                                            <button onClick={() => { setImagePickerTarget('hero'); setImagePickerOpen(true); }} className="flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"><span className="material-icons text-sm">folder_open</span>Browse</button>
                                        </div>
                                        <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${isDark ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30' : 'bg-primary/10 text-primary hover:bg-primary/20'} ${uploadingHero ? 'opacity-50 pointer-events-none' : ''}`}>
                                            <span className="material-icons text-sm">{uploadingHero ? 'hourglass_empty' : 'upload'}</span>
                                            <span className="text-sm font-medium">{uploadingHero ? 'Uploading...' : 'Upload Image'}</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                disabled={uploadingHero}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) uploadImage(file, 'hero');
                                                    e.target.value = '';
                                                }}
                                            />
                                        </label>
                                    </div>
                                    {destination.landingPage?.heroImage && <img src={destination.landingPage.heroImage} alt="Hero Preview" className="mt-2 h-32 object-cover rounded-lg" />}
                                </div>
                            </div>
                        </div>

                        {/* Pexels Stock Image Search */}
                        <div className={cardClass}>
                            <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                <span className="material-icons text-green-500">image_search</span>
                                Pexels Stock Image Search
                            </h3>
                            <div className="flex gap-2 mb-4">
                                <input
                                    type="text"
                                    value={pexelsQuery}
                                    onChange={e => setPexelsQuery(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && searchPexels(1)}
                                    className={`${inputClass} flex-1`}
                                    placeholder={`Search Pexels... (e.g. "${getLocalizedText(destination.title, 'en')} beach")`}
                                />
                                <button
                                    onClick={() => searchPexels(1)}
                                    disabled={pexelsLoading || !pexelsQuery.trim()}
                                    className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                                >
                                    <span className="material-icons text-sm">{pexelsLoading ? 'hourglass_empty' : 'search'}</span>
                                    {pexelsLoading ? 'Searching...' : 'Search'}
                                </button>
                            </div>

                            {pexelsResults.length > 0 && (
                                <>
                                    <p className={`text-xs mb-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                        {pexelsTotalResults} results found • Page {pexelsPage} • Click an image to use it
                                    </p>
                                    <div className="grid grid-cols-3 gap-3 mb-4">
                                        {pexelsResults.map((img: any) => (
                                            <div key={img.id} className={`relative rounded-xl overflow-hidden group cursor-pointer ${isDark ? 'bg-slate-700' : 'bg-white border'} hover:ring-2 hover:ring-green-500 transition-all`}>
                                                <img
                                                    src={img.thumbnail || img.url}
                                                    alt={img.alt || 'Pexels image'}
                                                    className="w-full h-36 object-cover"
                                                    loading="lazy"
                                                />
                                                {/* Overlay with action buttons */}
                                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => downloadPexelsImage(img, 'main')}
                                                        disabled={pexelsDownloading === `${img.id}-main`}
                                                        className="px-3 py-1.5 bg-blue-500 text-white text-xs rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1 w-32 justify-center"
                                                    >
                                                        <span className="material-icons text-xs">{pexelsDownloading === `${img.id}-main` ? 'hourglass_empty' : 'photo'}</span>
                                                        Use as Main
                                                    </button>
                                                    <button
                                                        onClick={() => downloadPexelsImage(img, 'hero')}
                                                        disabled={pexelsDownloading === `${img.id}-hero`}
                                                        className="px-3 py-1.5 bg-purple-500 text-white text-xs rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center gap-1 w-32 justify-center"
                                                    >
                                                        <span className="material-icons text-xs">{pexelsDownloading === `${img.id}-hero` ? 'hourglass_empty' : 'panorama'}</span>
                                                        Use as Hero
                                                    </button>
                                                </div>
                                                {/* Photographer credit */}
                                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1">
                                                    <p className="text-white text-[10px] truncate">📷 {img.photographer || 'Unknown'}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Pagination */}
                                    <div className="flex justify-center gap-2">
                                        <button
                                            onClick={() => searchPexels(pexelsPage - 1)}
                                            disabled={pexelsPage <= 1 || pexelsLoading}
                                            className={`px-3 py-1 rounded text-sm ${isDark ? 'bg-slate-600 text-slate-200 hover:bg-slate-500 disabled:bg-slate-700' : 'bg-slate-200 text-slate-700 hover:bg-slate-300 disabled:bg-slate-100'} disabled:opacity-50`}
                                        >
                                            ← Previous
                                        </button>
                                        <span className={`px-3 py-1 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Page {pexelsPage}</span>
                                        <button
                                            onClick={() => searchPexels(pexelsPage + 1)}
                                            disabled={pexelsResults.length < 12 || pexelsLoading}
                                            className={`px-3 py-1 rounded text-sm ${isDark ? 'bg-slate-600 text-slate-200 hover:bg-slate-500 disabled:bg-slate-700' : 'bg-slate-200 text-slate-700 hover:bg-slate-300 disabled:bg-slate-100'} disabled:opacity-50`}
                                        >
                                            Next →
                                        </button>
                                    </div>
                                    <p className={`text-[10px] text-center mt-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                        Photos provided by <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer" className="underline">Pexels</a>
                                    </p>
                                </>
                            )}

                            {pexelsResults.length === 0 && !pexelsLoading && (
                                <div className={`text-center py-6 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                    <span className="material-icons text-3xl mb-2 block">image_search</span>
                                    <p className="text-sm">Search for stock images on Pexels to use as destination images</p>
                                </div>
                            )}
                        </div>

                        <div className={cardClass}>
                            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Description</h3>
                            <MultiLangEditor
                                label="Landing Page Description"
                                value={destination.landingPage?.description || { en: '', sv: '', da: '' }}
                                onChange={(lang, html) => updateLandingPage('description', { ...destination.landingPage?.description, [lang]: html })}
                                isDark={isDark}
                                rows={3}
                            />
                        </div>

                        <div className={cardClass}>
                            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Rich Content</h3>
                            <div className="space-y-6">
                                {/* History & Culture */}
                                <div className={`p-4 rounded-xl border ${isDark ? 'border-amber-500/30 bg-amber-500/5' : 'border-amber-200 bg-amber-50/50'}`}>
                                    <h4 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                                        <span className="material-icons text-sm">account_balance</span>
                                        History & Culture
                                    </h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className={labelClass}>Image</label>
                                            <div className="flex gap-2">
                                                <input type="text" value={destination.richContent?.history?.image || ''} onChange={e => updateRichContentImage('history', e.target.value)} className={`${inputClass} flex-1`} placeholder="Enter image URL or upload" />
                                                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${isDark ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'} ${uploadingRichImage === 'history' ? 'opacity-50 pointer-events-none' : ''}`}>
                                                    <span className="material-icons text-sm">{uploadingRichImage === 'history' ? 'hourglass_empty' : 'upload'}</span>
                                                    <span className="text-sm font-medium">{uploadingRichImage === 'history' ? 'Uploading...' : 'Upload'}</span>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        disabled={uploadingRichImage === 'history'}
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) uploadRichContentImage(file, 'history');
                                                            e.target.value = '';
                                                        }}
                                                    />
                                                </label>
                                            </div>
                                            {destination.richContent?.history?.image && <img src={destination.richContent.history.image} alt="History" className="mt-2 h-28 object-cover rounded-lg" />}
                                        </div>
                                        <MultiLangEditor
                                            label="History Description"
                                            value={{ en: destination.richContent?.history?.en || '', sv: destination.richContent?.history?.sv || '', da: destination.richContent?.history?.da || '' }}
                                            onChange={(lang, html) => updateRichContent('history', lang, html)}
                                            isDark={isDark}
                                            rows={4}
                                        />
                                    </div>
                                </div>

                                {/* Nature & Landscape */}
                                <div className={`p-4 rounded-xl border ${isDark ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-emerald-200 bg-emerald-50/50'}`}>
                                    <h4 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                                        <span className="material-icons text-sm">landscape</span>
                                        Nature & Landscape
                                    </h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className={labelClass}>Image</label>
                                            <div className="flex gap-2">
                                                <input type="text" value={destination.richContent?.nature?.image || ''} onChange={e => updateRichContentImage('nature', e.target.value)} className={`${inputClass} flex-1`} placeholder="Enter image URL or upload" />
                                                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${isDark ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'} ${uploadingRichImage === 'nature' ? 'opacity-50 pointer-events-none' : ''}`}>
                                                    <span className="material-icons text-sm">{uploadingRichImage === 'nature' ? 'hourglass_empty' : 'upload'}</span>
                                                    <span className="text-sm font-medium">{uploadingRichImage === 'nature' ? 'Uploading...' : 'Upload'}</span>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        disabled={uploadingRichImage === 'nature'}
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) uploadRichContentImage(file, 'nature');
                                                            e.target.value = '';
                                                        }}
                                                    />
                                                </label>
                                            </div>
                                            {destination.richContent?.nature?.image && <img src={destination.richContent.nature.image} alt="Nature" className="mt-2 h-28 object-cover rounded-lg" />}
                                        </div>
                                        <MultiLangEditor
                                            label="Nature Description"
                                            value={{ en: destination.richContent?.nature?.en || '', sv: destination.richContent?.nature?.sv || '', da: destination.richContent?.nature?.da || '' }}
                                            onChange={(lang, html) => updateRichContent('nature', lang, html)}
                                            isDark={isDark}
                                            rows={4}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* SerpAPI Images Tab */}
                {activeTab === 'serpapi' && (
                    <div className="max-w-6xl mx-auto">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    SerpAPI Destination Images
                                </h3>
                                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Fetch and approve images from Google Images for this destination
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => fetchSerpApiImages(false)}
                                    disabled={serpApiLoading}
                                    className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-200 hover:bg-slate-300'} rounded-lg`}
                                >
                                    <span className="material-icons text-sm">{serpApiLoading ? 'hourglass_empty' : 'cloud_download'}</span>
                                    {serpApiLoading ? (language === 'sv' ? 'Laddar...' : 'Loading...') : (language === 'sv' ? 'Hämta bilder' : 'Fetch Images')}
                                </button>
                                <button
                                    onClick={() => fetchSerpApiImages(true)}
                                    disabled={serpApiLoading}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                                >
                                    <span className="material-icons text-sm">refresh</span>
                                    Refresh
                                </button>
                            </div>
                        </div>

                        {serpApiImages.length === 0 ? (
                            <div className={`text-center py-12 ${cardClass}`}>
                                <span className="material-icons text-5xl text-slate-400 mb-4">image_search</span>
                                <p className={`${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                    No images loaded yet. Click "Fetch Images" to get destination images from SerpAPI.
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-4 gap-4">
                                {serpApiImages.map((img, index) => (
                                    <div key={index} className={`relative rounded-xl overflow-hidden ${img.approved ? 'ring-2 ring-green-500' : ''} ${isDark ? 'bg-slate-700' : 'bg-white border'}`}>
                                        <img
                                            src={img.thumbnail || img.url}
                                            alt={img.title || `Image ${index + 1}`}
                                            className="w-full h-40 object-cover"
                                        />
                                        {img.approved && (
                                            <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                                                <span className="material-icons text-xs">check</span>
                                                {img.usedAs || 'Approved'}
                                            </div>
                                        )}
                                        <div className="p-3">
                                            <p className={`text-xs truncate mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                                {img.source || 'Unknown source'}
                                            </p>
                                            <div className="flex gap-2">
                                                {!img.approved ? (
                                                    <>
                                                        <button
                                                            onClick={() => approveImage(index, true, 'gallery')}
                                                            className="flex-1 text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                                                        >
                                                            Approve
                                                        </button>
                                                        <button
                                                            onClick={() => approveImage(index, true, 'hero')}
                                                            className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                                        >
                                                            Hero
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        onClick={() => approveImage(index, false)}
                                                        className="flex-1 text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                                    >
                                                        Revoke
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* TripAdvisor Tab */}
                {activeTab === 'tripadvisor' && (
                    <div className="max-w-6xl mx-auto">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    TripAdvisor Data
                                </h3>
                                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Attractions and restaurants from TripAdvisor
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => fetchTripAdvisorData(false)}
                                    disabled={tripAdvisorLoading}
                                    className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-200 hover:bg-slate-300'} rounded-lg`}
                                >
                                    <span className="material-icons text-sm">{tripAdvisorLoading ? 'hourglass_empty' : 'cloud_download'}</span>
                                    {tripAdvisorLoading ? (language === 'sv' ? 'Laddar...' : 'Loading...') : (language === 'sv' ? 'Hämta data' : 'Fetch Data')}
                                </button>
                                <button
                                    onClick={() => fetchTripAdvisorData(true)}
                                    disabled={tripAdvisorLoading}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                                >
                                    <span className="material-icons text-sm">refresh</span>
                                    Refresh
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                            {/* Attractions */}
                            <div>
                                <h4 className={`text-md font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    <span className="material-icons text-amber-500">place</span>
                                    Attractions ({tripAdvisorData.attractions.length})
                                </h4>
                                {tripAdvisorData.attractions.length === 0 ? (
                                    <div className={`text-center py-8 ${cardClass}`}>
                                        <p className={`${isDark ? 'text-slate-400' : 'text-slate-500'}`}>No attractions loaded</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {tripAdvisorData.attractions.map((attr, index) => (
                                            <div key={index} className={`p-4 rounded-lg ${attr.approved ? 'ring-2 ring-green-500' : ''} ${isDark ? 'bg-slate-700' : 'bg-white border'}`}>
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h5 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{attr.name}</h5>
                                                        <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{attr.snippet?.slice(0, 100)}...</p>
                                                        {attr.rating && (
                                                            <p className="text-xs mt-1 text-amber-500">⭐ {attr.rating} ({attr.reviewCount} reviews)</p>
                                                        )}
                                                    </div>
                                                    {attr.approved && (
                                                        <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs">✓</span>
                                                    )}
                                                </div>
                                                <div className="flex gap-2 mt-3">
                                                    {!attr.approved ? (
                                                        <button
                                                            onClick={() => approvePOI('attraction', index, true)}
                                                            className="text-xs px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                                                        >
                                                            Approve
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => approvePOI('attraction', index, false)}
                                                            className="text-xs px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                                        >
                                                            Revoke
                                                        </button>
                                                    )}
                                                    <a href={attr.link} target="_blank" rel="noopener noreferrer" className={`text-xs px-3 py-1 rounded ${isDark ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700'}`}>
                                                        View
                                                    </a>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Restaurants */}
                            <div>
                                <h4 className={`text-md font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    <span className="material-icons text-rose-500">restaurant</span>
                                    Restaurants ({tripAdvisorData.restaurants.length})
                                </h4>
                                {tripAdvisorData.restaurants.length === 0 ? (
                                    <div className={`text-center py-8 ${cardClass}`}>
                                        <p className={`${isDark ? 'text-slate-400' : 'text-slate-500'}`}>No restaurants loaded</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {tripAdvisorData.restaurants.map((rest, index) => (
                                            <div key={index} className={`p-4 rounded-lg ${rest.approved ? 'ring-2 ring-green-500' : ''} ${isDark ? 'bg-slate-700' : 'bg-white border'}`}>
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h5 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{rest.name}</h5>
                                                        {rest.cuisines && rest.cuisines.length > 0 && (
                                                            <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{rest.cuisines.join(', ')}</p>
                                                        )}
                                                        {rest.rating && (
                                                            <p className="text-xs mt-1 text-amber-500">⭐ {rest.rating} ({rest.reviewCount} reviews)</p>
                                                        )}
                                                    </div>
                                                    {rest.approved && (
                                                        <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs">✓</span>
                                                    )}
                                                </div>
                                                <div className="flex gap-2 mt-3">
                                                    {!rest.approved ? (
                                                        <button
                                                            onClick={() => approvePOI('restaurant', index, true)}
                                                            className="text-xs px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                                                        >
                                                            Approve
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => approvePOI('restaurant', index, false)}
                                                            className="text-xs px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                                        >
                                                            Revoke
                                                        </button>
                                                    )}
                                                    <a href={rest.link} target="_blank" rel="noopener noreferrer" className={`text-xs px-3 py-1 rounded ${isDark ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700'}`}>
                                                        View
                                                    </a>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Things To Do Tab */}
                {activeTab === 'activities' && (
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                Things To Do ({destination.sections?.thingsToDo?.length || 0})
                            </h3>
                            <button onClick={addActivity} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">
                                <span className="material-icons text-sm">add</span>
                                Add Activity
                            </button>
                        </div>
                        <div className="space-y-4">
                            {(destination.sections?.thingsToDo || []).map((activity, index) => (
                                <div key={activity.id} className={cardClass}>
                                    <div className="flex items-start justify-between mb-4">
                                        <h4 className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{getLocalizedText(activity.title, 'en')}</h4>
                                        <button onClick={() => removeActivity(index)} className="text-red-500 hover:text-red-700">
                                            <span className="material-icons">delete</span>
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label className={labelClass}>Title (EN)</label>
                                            <input type="text" value={getLocalizedText(activity.title, 'en') || ''} onChange={e => updateActivity(index, 'title.en', e.target.value)} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Title (SV)</label>
                                            <input type="text" value={getLocalizedText(activity.title, 'sv') || ''} onChange={e => updateActivity(index, 'title.sv', e.target.value)} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Title (DA)</label>
                                            <input type="text" value={getLocalizedText(activity.title, 'da') || ''} onChange={e => updateActivity(index, 'title.da', e.target.value)} className={inputClass} />
                                        </div>
                                        <div className="col-span-3">
                                            <MultiLangEditor
                                                label="Description"
                                                value={activity.description || { en: '', sv: '', da: '' }}
                                                onChange={(lang, html) => updateActivity(index, `description.${lang}`, html)}
                                                isDark={isDark}
                                                rows={3}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Image URL</label>
                                            <div className="flex gap-2">
                                                <input type="text" value={activity.image || ''} onChange={e => updateActivity(index, 'image', e.target.value)} className={`${inputClass} flex-1`} placeholder="Enter URL or upload" />
                                                <button
                                                    type="button"
                                                    onClick={() => triggerContentImageUpload('activity', index)}
                                                    disabled={uploadingContent === `activity-${index}`}
                                                    className={`px-3 py-2 rounded-lg ${isDark ? 'bg-slate-600 hover:bg-slate-500' : 'bg-slate-200 hover:bg-slate-300'} transition-colors disabled:opacity-50`}
                                                    title="Upload image"
                                                >
                                                    <span className="material-icons text-sm">
                                                        {uploadingContent === `activity-${index}` ? 'hourglass_empty' : 'upload'}
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setImagePickerTarget({ type: 'activity', index }); setImagePickerOpen(true); }}
                                                    className={`px-3 py-2 rounded-lg ${isDark ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'} transition-colors`}
                                                    title="Browse file manager"
                                                >
                                                    <span className="material-icons text-sm">folder_open</span>
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Category</label>
                                            <select value={activity.category} onChange={e => updateActivity(index, 'category', e.target.value)} className={inputClass}>
                                                <option value="adventure">Adventure</option>
                                                <option value="culture">Culture</option>
                                                <option value="nature">Nature</option>
                                                <option value="family">Family</option>
                                                <option value="nightlife">Nightlife</option>
                                                <option value="shopping">Shopping</option>
                                                <option value="wellness">Wellness</option>
                                                <option value="food">Food</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Duration</label>
                                            <input type="text" value={activity.duration || ''} onChange={e => updateActivity(index, 'duration', e.target.value)} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Price</label>
                                            <input type="text" value={activity.price || ''} onChange={e => updateActivity(index, 'price', e.target.value)} className={inputClass} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Attractions Tab */}
                {activeTab === 'attractions' && (
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                Attractions ({destination.sections?.attractions?.length || 0})
                            </h3>
                            <button onClick={addAttraction} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">
                                <span className="material-icons text-sm">add</span>
                                Add Attraction
                            </button>
                        </div>
                        <div className="space-y-4">
                            {(destination.sections?.attractions || []).map((attr, index) => (
                                <div key={attr.id} className={cardClass}>
                                    <div className="flex items-start justify-between mb-4">
                                        <h4 className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{getLocalizedText(attr.title, 'en')}</h4>
                                        <button onClick={() => removeAttraction(index)} className="text-red-500 hover:text-red-700">
                                            <span className="material-icons">delete</span>
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label className={labelClass}>Title (EN)</label>
                                            <input type="text" value={getLocalizedText(attr.title, 'en') || ''} onChange={e => updateAttraction(index, 'title.en', e.target.value)} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Title (SV)</label>
                                            <input type="text" value={getLocalizedText(attr.title, 'sv') || ''} onChange={e => updateAttraction(index, 'title.sv', e.target.value)} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Title (DA)</label>
                                            <input type="text" value={getLocalizedText(attr.title, 'da') || ''} onChange={e => updateAttraction(index, 'title.da', e.target.value)} className={inputClass} />
                                        </div>
                                        <div className="col-span-3">
                                            <MultiLangEditor
                                                label="Description"
                                                value={attr.description || { en: '', sv: '', da: '' }}
                                                onChange={(lang, html) => updateAttraction(index, `description.${lang}`, html)}
                                                isDark={isDark}
                                                rows={3}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Image URL</label>
                                            <div className="flex gap-2">
                                                <input type="text" value={attr.image || ''} onChange={e => updateAttraction(index, 'image', e.target.value)} className={`${inputClass} flex-1`} placeholder="Enter URL or upload" />
                                                <button
                                                    type="button"
                                                    onClick={() => triggerContentImageUpload('attraction', index)}
                                                    disabled={uploadingContent === `attraction-${index}`}
                                                    className={`px-3 py-2 rounded-lg ${isDark ? 'bg-slate-600 hover:bg-slate-500' : 'bg-slate-200 hover:bg-slate-300'} transition-colors disabled:opacity-50`}
                                                    title="Upload image"
                                                >
                                                    <span className="material-icons text-sm">
                                                        {uploadingContent === `attraction-${index}` ? 'hourglass_empty' : 'upload'}
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setImagePickerTarget({ type: 'attraction', index }); setImagePickerOpen(true); }}
                                                    className={`px-3 py-2 rounded-lg ${isDark ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'} transition-colors`}
                                                    title="Browse file manager"
                                                >
                                                    <span className="material-icons text-sm">folder_open</span>
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Category</label>
                                            <select value={attr.category} onChange={e => updateAttraction(index, 'category', e.target.value)} className={inputClass}>
                                                <option value="landmark">Landmark</option>
                                                <option value="museum">Museum</option>
                                                <option value="beach">Beach</option>
                                                <option value="park">Park</option>
                                                <option value="historic">Historic</option>
                                                <option value="religious">Religious</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Address</label>
                                            <input type="text" value={attr.address || ''} onChange={e => updateAttraction(index, 'address', e.target.value)} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Entry Fee</label>
                                            <input type="text" value={attr.entryFee || ''} onChange={e => updateAttraction(index, 'entryFee', e.target.value)} className={inputClass} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Restaurants Tab */}
                {activeTab === 'restaurants' && (
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                Restaurants ({destination.sections?.restaurants?.length || 0})
                            </h3>
                            <div className="flex items-center gap-2">
                                {/* Import from TripAdvisor */}
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            setMessage({ type: 'info', text: 'Searching TripAdvisor for individual restaurants... This may take a moment.' });
                                            const importRes = await fetch(`/api/destinations/${destination.slug || destination._id}/import-tripadvisor-restaurants`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ maxRestaurants: 10 })
                                            });
                                            const importData = await importRes.json();
                                            
                                            if (!importData.success || !importData.restaurants?.length) {
                                                setMessage({ type: 'error', text: importData.error || 'No individual restaurants found on TripAdvisor' });
                                                return;
                                            }

                                            // Filter out already imported restaurants
                                            const existingNames = (destination.sections?.restaurants || []).map((r: any) => r.name?.toLowerCase().trim());
                                            const newRestaurants = importData.restaurants.filter((r: any) => 
                                                !existingNames.includes(r.name?.toLowerCase().trim())
                                            );

                                            if (newRestaurants.length === 0) {
                                                setMessage({ type: 'info', text: `All ${importData.restaurants.length} restaurants already imported` });
                                                return;
                                            }

                                            const current = [...(destination.sections?.restaurants || [])];
                                            for (const rest of newRestaurants) {
                                                current.push({
                                                    id: `rest-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                                                    ...rest
                                                });
                                            }
                                            updateSections('restaurants', current);
                                            setMessage({ type: 'success', text: `Imported ${newRestaurants.length} individual restaurants from TripAdvisor (${importData.totalFound} found, ${importData.totalProcessed} enriched)` });
                                        } catch (err: any) {
                                            setMessage({ type: 'error', text: `TripAdvisor import failed: ${err.message}` });
                                        }
                                    }}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-emerald-700 hover:bg-emerald-600 text-white' : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800'} transition-colors`}
                                >
                                    <span className="material-icons text-sm">travel_explore</span>
                                    Import from TripAdvisor
                                </button>
                                <button onClick={addRestaurant} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">
                                    <span className="material-icons text-sm">add</span>
                                    Add Restaurant
                                </button>
                            </div>
                        </div>
                        <div className="space-y-4">
                            {(destination.sections?.restaurants || []).map((rest, index) => (
                                <div key={rest.id} className={cardClass}>
                                    <div className="flex items-start justify-between mb-4">
                                        <h4 className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{rest.name}</h4>
                                        <div className="flex items-center gap-2">
                                            {/* Auto-fill from TripAdvisor */}
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    try {
                                                        setMessage({ type: 'info', text: `Searching TripAdvisor + Google Places for "${rest.name}"...` });
                                                        const res = await fetch(`/api/destinations/${destination.slug || destination.id}/enrich-restaurant`, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({ restaurantIndex: index, restaurantName: rest.name })
                                                        });
                                                        const data = await res.json();
                                                        if (data.success && data.enrichedRestaurant) {
                                                            const enriched = data.enrichedRestaurant;
                                                            const restaurants = [...(destination.sections?.restaurants || [])];
                                                            const merged = { ...restaurants[index] };
                                                            // Merge without overwriting existing non-empty values
                                                            if (!merged.image && enriched.image) merged.image = enriched.image;
                                                            if (!merged.rating && enriched.rating) merged.rating = enriched.rating;
                                                            if (!merged.cuisine && enriched.cuisine) merged.cuisine = enriched.cuisine;
                                                            if (!merged.address && enriched.address) merged.address = enriched.address;
                                                            if (!merged.phone && enriched.phone) merged.phone = enriched.phone;
                                                            if (!merged.website && enriched.website) merged.website = enriched.website;
                                                            if (!merged.openingHours && enriched.openingHours) merged.openingHours = enriched.openingHours;
                                                            if ((!merged.description?.en) && enriched.description?.en) {
                                                                merged.description = { ...merged.description, en: enriched.description.en };
                                                            }
                                                            if (enriched.tripAdvisorUrl) merged.tripAdvisorUrl = enriched.tripAdvisorUrl;
                                                            if (enriched.coordinates && !(merged as any).coordinates?.lat) (merged as any).coordinates = enriched.coordinates;
                                                            if (enriched.mapUrl && !(merged as any).mapUrl) (merged as any).mapUrl = enriched.mapUrl;
                                                            // Gallery: append new photos to existing
                                                            if (enriched.gallery?.length) {
                                                                const existing = merged.gallery || [];
                                                                const newPhotos = enriched.gallery.filter((g: string) => !existing.includes(g));
                                                                merged.gallery = [...existing, ...newPhotos];
                                                            }
                                                            restaurants[index] = merged;
                                                            updateSections('restaurants', restaurants);
                                                            const fieldCount = Object.keys(enriched).length;
                                                            setMessage({ type: 'success', text: `Restaurant enriched (${fieldCount} fields) via ${data.source}` });
                                                        } else {
                                                            setMessage({ type: 'error', text: data.error || 'No data found' });
                                                        }
                                                    } catch (err: any) {
                                                        setMessage({ type: 'error', text: `Enrichment failed: ${err.message}` });
                                                    }
                                                }}
                                                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium ${isDark ? 'bg-emerald-700 hover:bg-emerald-600 text-white' : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800'} transition-colors`}
                                                title="Auto-fill missing fields from TripAdvisor + Google Places"
                                            >
                                                <span className="material-icons text-sm">auto_fix_high</span>
                                                Auto-Fill
                                            </button>
                                            {/* Fetch Gallery from Google Places */}
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    try {
                                                        setMessage({ type: 'info', text: `Fetching gallery for "${rest.name}" from Google Places...` });
                                                        const res = await fetch(`/api/destinations/${destination.slug || destination.id}/restaurant-gallery`, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({ restaurantName: rest.name, maxPhotos: 10 })
                                                        });
                                                        const data = await res.json();
                                                        if (data.success && data.photos?.length) {
                                                            const restaurants = [...(destination.sections?.restaurants || [])];
                                                            const existing = restaurants[index].gallery || [];
                                                            const newPhotos = data.photos.filter((p: string) => !existing.includes(p));
                                                            restaurants[index] = { ...restaurants[index], gallery: [...existing, ...newPhotos] };
                                                            updateSections('restaurants', restaurants);
                                                            setMessage({ type: 'success', text: `Added ${newPhotos.length} gallery photos from Google Places` });
                                                        } else {
                                                            setMessage({ type: 'error', text: data.error || 'No photos found' });
                                                        }
                                                    } catch (err: any) {
                                                        setMessage({ type: 'error', text: `Gallery fetch failed: ${err.message}` });
                                                    }
                                                }}
                                                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium ${isDark ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-100 hover:bg-blue-200 text-blue-800'} transition-colors`}
                                                title="Fetch gallery photos from Google Places"
                                            >
                                                <span className="material-icons text-sm">collections</span>
                                                Gallery
                                            </button>
                                            <button onClick={() => removeRestaurant(index)} className="text-red-500 hover:text-red-700">
                                                <span className="material-icons">delete</span>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelClass}>Name</label>
                                            <input type="text" value={rest.name} onChange={e => updateRestaurant(index, 'name', e.target.value)} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Cuisine</label>
                                            <input type="text" value={rest.cuisine} onChange={e => updateRestaurant(index, 'cuisine', e.target.value)} className={inputClass} />
                                        </div>
                                        <div className="col-span-2">
                                            <MultiLangEditor
                                                label="Description"
                                                value={rest.description || { en: '', sv: '', da: '' }}
                                                onChange={(lang, html) => updateRestaurant(index, `description.${lang}`, html)}
                                                isDark={isDark}
                                                rows={3}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Main Image URL</label>
                                            <div className="flex gap-2">
                                                <input type="text" value={rest.image || ''} onChange={e => updateRestaurant(index, 'image', e.target.value)} className={`${inputClass} flex-1`} placeholder="Enter URL or upload" />
                                                <button
                                                    type="button"
                                                    onClick={() => triggerContentImageUpload('restaurant', index)}
                                                    disabled={uploadingContent === `restaurant-${index}`}
                                                    className={`px-3 py-2 rounded-lg ${isDark ? 'bg-slate-600 hover:bg-slate-500' : 'bg-slate-200 hover:bg-slate-300'} transition-colors disabled:opacity-50`}
                                                    title="Upload image"
                                                >
                                                    <span className="material-icons text-sm">
                                                        {uploadingContent === `restaurant-${index}` ? 'hourglass_empty' : 'upload'}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Price Range</label>
                                            <select value={rest.priceRange} onChange={e => updateRestaurant(index, 'priceRange', e.target.value)} className={inputClass}>
                                                <option value="$">$</option>
                                                <option value="$$">$$</option>
                                                <option value="$$$">$$$</option>
                                                <option value="$$$$">$$$$</option>
                                            </select>
                                        </div>
                                        
                                        {/* Gallery Management */}
                                        <div className="col-span-2">
                                            {/* Gallery Header */}
                                            <div className="flex items-center justify-between mb-2">
                                                <label className={labelClass + ' mb-0'}>
                                                    <span className="flex items-center gap-1">
                                                        <span className="material-icons text-sm">collections</span>
                                                        Gallery ({rest.gallery?.length || 0} images)
                                                        {(rest.gallery || []).some((u: string) => u.includes('maps.googleapis.com/maps/api/place/photo')) && (
                                                            <span className="ml-1 px-1.5 py-0.5 text-xs font-bold bg-amber-100 text-amber-700 rounded-full flex items-center gap-0.5">
                                                                <span className="material-icons" style={{fontSize:'12px'}}>warning</span>
                                                                Expired Photos
                                                            </span>
                                                        )}
                                                    </span>
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    {/* Clear Expired Google Photos */}
                                                    {(rest.gallery || []).some((u: string) => u.includes('maps.googleapis.com/maps/api/place/photo')) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const cleaned = (rest.gallery || []).filter((u: string) => !u.includes('maps.googleapis.com/maps/api/place/photo'));
                                                                updateRestaurant(index, 'gallery', cleaned);
                                                                setMessage({ type: 'success', text: `Removed expired Google Photos from gallery.` });
                                                            }}
                                                            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-100 hover:bg-amber-200 text-amber-800 transition-colors"
                                                            title="Remove expired Google Places photos"
                                                        >
                                                            <span className="material-icons" style={{fontSize:'14px'}}>auto_delete</span>
                                                            Clear Expired
                                                        </button>
                                                    )}
                                                    {/* Pexels Search Toggle */}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (restPexelsOpen === index) {
                                                                setRestPexelsOpen(null);
                                                                setRestPexelsResults([]);
                                                            } else {
                                                                setRestPexelsOpen(index);
                                                                setRestPexelsQuery(rest.name || '');
                                                                setRestPexelsResults([]);
                                                            }
                                                        }}
                                                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                                                            restPexelsOpen === index
                                                                ? 'bg-rose-500 text-white hover:bg-rose-600'
                                                                : isDark ? 'bg-violet-700 hover:bg-violet-600 text-white' : 'bg-violet-100 hover:bg-violet-200 text-violet-800'
                                                        }`}
                                                        title="Search Pexels for new images"
                                                    >
                                                        <span className="material-icons" style={{fontSize:'14px'}}>image_search</span>
                                                        {restPexelsOpen === index ? 'Close Search' : 'Search Pexels'}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Gallery Thumbnails */}
                                            {rest.gallery && rest.gallery.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mb-3">
                                                    {rest.gallery.map((img: string, gi: number) => {
                                                        const isExpired = img.includes('maps.googleapis.com/maps/api/place/photo');
                                                        return (
                                                            <div key={gi} className={`relative group w-20 h-20 rounded-lg overflow-hidden border-2 ${
                                                                isExpired ? 'border-amber-400' : 'border-slate-200'
                                                            }`}>
                                                                {isExpired ? (
                                                                    <div className="w-full h-full bg-amber-50 flex flex-col items-center justify-center p-1">
                                                                        <span className="material-icons text-amber-500" style={{fontSize:'20px'}}>broken_image</span>
                                                                        <span className="text-xs text-amber-600 text-center leading-tight">Expired</span>
                                                                    </div>
                                                                ) : (
                                                                    <img src={img} alt={`Gallery ${gi + 1}`} className="w-full h-full object-cover" />
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const newGallery = [...(rest.gallery || [])];
                                                                        newGallery.splice(gi, 1);
                                                                        updateRestaurant(index, 'gallery', newGallery);
                                                                    }}
                                                                    className="absolute inset-0 bg-red-500/70 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                                                    title="Remove image"
                                                                >
                                                                    <span className="material-icons text-sm">close</span>
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* Pexels Image Search Panel */}
                                            {restPexelsOpen === index && (
                                                <div className={`mb-3 p-3 rounded-xl border ${
                                                    isDark ? 'bg-slate-800 border-slate-600' : 'bg-violet-50 border-violet-200'
                                                }`}>
                                                    <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>
                                                        🔍 Search Pexels — add photos to gallery
                                                    </p>
                                                    <div className="flex gap-2 mb-3">
                                                        <input
                                                            type="text"
                                                            value={restPexelsQuery}
                                                            onChange={e => setRestPexelsQuery(e.target.value)}
                                                            onKeyDown={async (e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    if (!restPexelsQuery.trim()) return;
                                                                    setRestPexelsLoading(true);
                                                                    setRestPexelsResults([]);
                                                                    try {
                                                                        const res = await fetch(`/api/media/stock/search?query=${encodeURIComponent(restPexelsQuery)}&source=pexels&page=1&perPage=12&orientation=landscape`);
                                                                        const data = await res.json();
                                                                        if (data.success && data.data) {
                                                                            setRestPexelsResults(data.data.images || []);
                                                                        } else {
                                                                            setMessage({ type: 'error', text: 'Pexels search failed' });
                                                                        }
                                                                    } catch { setMessage({ type: 'error', text: 'Pexels search error' }); }
                                                                    finally { setRestPexelsLoading(false); }
                                                                }
                                                            }}
                                                            placeholder="Search Pexels (press Enter)"
                                                            className={inputClass}
                                                        />
                                                        <button
                                                            type="button"
                                                            disabled={restPexelsLoading}
                                                            onClick={async () => {
                                                                if (!restPexelsQuery.trim()) return;
                                                                setRestPexelsLoading(true);
                                                                setRestPexelsResults([]);
                                                                try {
                                                                    const res = await fetch(`/api/media/stock/search?query=${encodeURIComponent(restPexelsQuery)}&source=pexels&page=1&perPage=12&orientation=landscape`);
                                                                    const data = await res.json();
                                                                    if (data.success && data.data) setRestPexelsResults(data.data.images || []);
                                                                    else setMessage({ type: 'error', text: 'Pexels search failed' });
                                                                } catch { setMessage({ type: 'error', text: 'Pexels search error' }); }
                                                                finally { setRestPexelsLoading(false); }
                                                            }}
                                                            className="px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1 text-sm"
                                                        >
                                                            {restPexelsLoading
                                                                ? <span className="material-icons text-sm animate-spin">autorenew</span>
                                                                : <span className="material-icons text-sm">search</span>
                                                            }
                                                            Search
                                                        </button>
                                                    </div>
                                                    {restPexelsResults.length > 0 && (
                                                        <div className="grid grid-cols-4 gap-2">
                                                            {restPexelsResults.map((img: any) => (
                                                                <div key={img.id} className="relative group rounded-lg overflow-hidden aspect-square bg-slate-200 cursor-pointer"
                                                                    onClick={async () => {
                                                                        setRestPexelsDownloading(img.id);
                                                                        try {
                                                                            const res = await fetch('/api/media/stock/download', {
                                                                                method: 'POST',
                                                                                headers: { 'Content-Type': 'application/json' },
                                                                                body: JSON.stringify({
                                                                                    downloadUrl: img.downloadUrl || img.url,
                                                                                    source: 'pexels',
                                                                                    id: img.id,
                                                                                    alt: img.alt || restPexelsQuery,
                                                                                    credit: img.photographer
                                                                                })
                                                                            });
                                                                            const data = await res.json();
                                                                            if (data.success && data.localUrl) {
                                                                                const newGallery = [...(destination.sections?.restaurants?.[index]?.gallery || []), data.localUrl];
                                                                                updateRestaurant(index, 'gallery', newGallery);
                                                                                setMessage({ type: 'success', text: `Photo by ${img.photographer} added to gallery!` });
                                                                            } else {
                                                                                setMessage({ type: 'error', text: data.error || 'Download failed' });
                                                                            }
                                                                        } catch { setMessage({ type: 'error', text: 'Download error' }); }
                                                                        finally { setRestPexelsDownloading(null); }
                                                                    }}
                                                                >
                                                                    <img src={img.previewUrl || img.url} alt={img.alt || ''} className="w-full h-full object-cover" />
                                                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                                                                        {restPexelsDownloading === img.id
                                                                            ? <span className="material-icons text-white animate-spin">autorenew</span>
                                                                            : <>
                                                                                <span className="material-icons text-white">add_photo_alternate</span>
                                                                                <span className="text-white text-xs text-center px-1">{img.photographer}</span>
                                                                            </>
                                                                        }
                                                                    </div>
                                                                    {/* Use as main image */}
                                                                    <button
                                                                        type="button"
                                                                        title="Set as main image"
                                                                        onClick={async (e) => {
                                                                            e.stopPropagation();
                                                                            setRestPexelsDownloading(`main-${img.id}`);
                                                                            try {
                                                                                const res = await fetch('/api/media/stock/download', {
                                                                                    method: 'POST',
                                                                                    headers: { 'Content-Type': 'application/json' },
                                                                                    body: JSON.stringify({
                                                                                        downloadUrl: img.downloadUrl || img.url,
                                                                                        source: 'pexels',
                                                                                        id: img.id,
                                                                                        alt: img.alt || restPexelsQuery,
                                                                                        credit: img.photographer
                                                                                    })
                                                                                });
                                                                                const data = await res.json();
                                                                                if (data.success && data.localUrl) {
                                                                                    updateRestaurant(index, 'image', data.localUrl);
                                                                                    setMessage({ type: 'success', text: `Main image updated from Pexels!` });
                                                                                } else {
                                                                                    setMessage({ type: 'error', text: data.error || 'Download failed' });
                                                                                }
                                                                            } catch { setMessage({ type: 'error', text: 'Download error' }); }
                                                                            finally { setRestPexelsDownloading(null); }
                                                                        }}
                                                                        className="absolute top-1 right-1 p-0.5 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    >
                                                                        <span className="material-icons" style={{fontSize:'12px'}}>star</span>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {restPexelsResults.length === 0 && !restPexelsLoading && (
                                                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'} text-center py-2`}>
                                                            Enter a search term and press Enter or click Search
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {/* Add Gallery Image — URL paste + upload */}
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    placeholder="Paste image URL and press Enter"
                                                    className={`${inputClass} flex-1`}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            const url = (e.target as HTMLInputElement).value.trim();
                                                            if (url) {
                                                                const newGallery = [...(rest.gallery || []), url];
                                                                updateRestaurant(index, 'gallery', newGallery);
                                                                (e.target as HTMLInputElement).value = '';
                                                            }
                                                        }
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const input = document.createElement('input');
                                                        input.type = 'file';
                                                        input.accept = 'image/*';
                                                        input.multiple = true;
                                                        input.onchange = async (ev) => {
                                                            const files = (ev.target as HTMLInputElement).files;
                                                            if (!files) return;
                                                            for (const file of Array.from(files)) {
                                                                const formData = new FormData();
                                                                formData.append('image', file);
                                                                formData.append('category', 'restaurant');
                                                                try {
                                                                    const resp = await fetch('/api/media/upload', { method: 'POST', body: formData });
                                                                    const data = await resp.json();
                                                                    if (data.imageUrl) {
                                                                        const currentGallery = [...(destination.sections?.restaurants?.[index]?.gallery || []), data.imageUrl];
                                                                        updateRestaurant(index, 'gallery', currentGallery);
                                                                    }
                                                                } catch (err) {
                                                                    console.error('Gallery upload error:', err);
                                                                }
                                                            }
                                                        };
                                                        input.click();
                                                    }}
                                                    className={`px-3 py-2 rounded-lg ${isDark ? 'bg-slate-600 hover:bg-slate-500' : 'bg-slate-200 hover:bg-slate-300'} transition-colors`}
                                                    title="Upload gallery images from device"
                                                >
                                                    <span className="material-icons text-sm">add_photo_alternate</span>
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label className={labelClass}>Rating</label>
                                            <input type="number" step="0.1" min="0" max="5" value={rest.rating || ''} onChange={e => updateRestaurant(index, 'rating', parseFloat(e.target.value))} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Address</label>
                                            <input type="text" value={rest.address || ''} onChange={e => updateRestaurant(index, 'address', e.target.value)} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Website</label>
                                            <input type="url" value={rest.website || ''} onChange={e => updateRestaurant(index, 'website', e.target.value)} className={inputClass} placeholder="https://..." />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Phone</label>
                                            <input type="tel" value={rest.phone || ''} onChange={e => updateRestaurant(index, 'phone', e.target.value)} className={inputClass} placeholder="+46..." />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Opening Hours</label>
                                            <input type="text" value={rest.openingHours || ''} onChange={e => updateRestaurant(index, 'openingHours', e.target.value)} className={inputClass} placeholder="Mon-Sun 11:00-23:00" />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Video URL</label>
                                            <input type="url" value={rest.videoUrl || ''} onChange={e => updateRestaurant(index, 'videoUrl', e.target.value)} className={inputClass} placeholder="YouTube or Vimeo URL" />
                                        </div>

                                        {/* Map & Coordinates */}
                                        <div>
                                            <label className={labelClass}>
                                                <span className="flex items-center gap-1">
                                                    <span className="material-icons text-sm">place</span>
                                                    Latitude
                                                </span>
                                            </label>
                                            <input type="number" step="0.000001" value={rest.coordinates?.lat || ''} onChange={e => {
                                                const lat = parseFloat(e.target.value);
                                                const lng = rest.coordinates?.lng || 0;
                                                updateRestaurant(index, 'coordinates', { lat, lng });
                                            }} className={inputClass} placeholder="e.g. 37.0381" />
                                        </div>
                                        <div>
                                            <label className={labelClass}>
                                                <span className="flex items-center gap-1">
                                                    <span className="material-icons text-sm">place</span>
                                                    Longitude
                                                </span>
                                            </label>
                                            <input type="number" step="0.000001" value={rest.coordinates?.lng || ''} onChange={e => {
                                                const lng = parseFloat(e.target.value);
                                                const lat = rest.coordinates?.lat || 0;
                                                updateRestaurant(index, 'coordinates', { lat, lng });
                                            }} className={inputClass} placeholder="e.g. 27.4292" />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Google Maps URL</label>
                                            <input type="url" value={(rest as any).mapUrl || ''} onChange={e => updateRestaurant(index, 'mapUrl', e.target.value)} className={inputClass} placeholder="https://maps.google.com/..." />
                                        </div>
                                        <div>
                                            <label className={labelClass}>TripAdvisor URL</label>
                                            <input type="url" value={(rest as any).tripAdvisorUrl || ''} onChange={e => updateRestaurant(index, 'tripAdvisorUrl', e.target.value)} className={inputClass} placeholder="https://tripadvisor.com/..." />
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <label className="flex items-center gap-2">
                                                <input type="checkbox" checked={rest.reservationEnabled || false} onChange={e => updateRestaurant(index, 'reservationEnabled', e.target.checked)} className="w-4 h-4" />
                                                <span className={labelClass}>Reservation Enabled</span>
                                            </label>
                                        </div>
                                        <div className="col-span-2">
                                            <MultiLangEditor
                                                label="AI Commentary"
                                                value={rest.aiComment || { en: '', sv: '', da: '' }}
                                                onChange={(lang, html) => updateRestaurant(index, `aiComment.${lang}`, html)}
                                                isDark={isDark}
                                                rows={3}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Itineraries Tab */}
                {activeTab === 'itineraries' && (
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                Itineraries ({destination.sections?.itineraries?.length || 0})
                            </h3>
                            <button onClick={addItinerary} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">
                                <span className="material-icons text-sm">add</span>
                                Add Itinerary
                            </button>
                        </div>
                        <div className="space-y-4">
                            {(destination.sections?.itineraries || []).map((itin, index) => (
                                <div key={itin.id} className={cardClass}>
                                    <div className="flex items-start justify-between mb-4">
                                        <h4 className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{getLocalizedText(itin.title, 'en')}</h4>
                                        <button onClick={() => removeItinerary(index)} className="text-red-500 hover:text-red-700">
                                            <span className="material-icons">delete</span>
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label className={labelClass}>Title (EN)</label>
                                            <input type="text" value={getLocalizedText(itin.title, 'en') || ''} onChange={e => updateItinerary(index, 'title.en', e.target.value)} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Title (SV)</label>
                                            <input type="text" value={getLocalizedText(itin.title, 'sv') || ''} onChange={e => updateItinerary(index, 'title.sv', e.target.value)} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Title (DA)</label>
                                            <input type="text" value={getLocalizedText(itin.title, 'da') || ''} onChange={e => updateItinerary(index, 'title.da', e.target.value)} className={inputClass} />
                                        </div>
                                        <div className="col-span-3">
                                            <MultiLangEditor
                                                label="Description"
                                                value={itin.description || { en: '', sv: '', da: '' }}
                                                onChange={(lang, html) => updateItinerary(index, `description.${lang}`, html)}
                                                isDark={isDark}
                                                rows={3}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Image URL</label>
                                            <div className="flex gap-2">
                                                <input type="text" value={itin.image} onChange={e => updateItinerary(index, 'image', e.target.value)} className={`${inputClass} flex-1`} placeholder="Enter URL or browse" />
                                                <button
                                                    type="button"
                                                    onClick={() => triggerContentImageUpload('itinerary', index)}
                                                    disabled={uploadingContent === `itinerary-${index}`}
                                                    className={`px-3 py-2 rounded-lg ${isDark ? 'bg-slate-600 hover:bg-slate-500' : 'bg-slate-200 hover:bg-slate-300'} transition-colors disabled:opacity-50`}
                                                    title="Upload image"
                                                >
                                                    <span className="material-icons text-sm">
                                                        {uploadingContent === `itinerary-${index}` ? 'hourglass_empty' : 'upload'}
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setImagePickerTarget({ type: 'itinerary', index }); setImagePickerOpen(true); }}
                                                    className={`px-3 py-2 rounded-lg ${isDark ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'} transition-colors`}
                                                    title="Browse file manager"
                                                >
                                                    <span className="material-icons text-sm">folder_open</span>
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Days</label>
                                            <input type="number" min="1" value={itin.days} onChange={e => updateItinerary(index, 'days', parseInt(e.target.value))} className={inputClass} />
                                        </div>
                                        <div className="col-span-2">
                                            <label className={labelClass}>Highlights (EN) - One per line</label>
                                            <textarea rows={3} value={(itin.highlights?.en || []).join('\n')} onChange={e => updateItinerary(index, 'highlights.en', e.target.value.split('\n').filter(h => h.trim()))} className={textareaClass} />
                                        </div>
                                        <div className="col-span-2">
                                            <label className={labelClass}>Highlights (SV) - One per line</label>
                                            <textarea rows={3} value={(itin.highlights?.sv || []).join('\n')} onChange={e => updateItinerary(index, 'highlights.sv', e.target.value.split('\n').filter(h => h.trim()))} className={textareaClass} />
                                        </div>
                                        <div className="col-span-2">
                                            <label className={labelClass}>Highlights (DA) - One per line</label>
                                            <textarea rows={3} value={(itin.highlights?.da || []).join('\n')} onChange={e => updateItinerary(index, 'highlights.da', e.target.value.split('\n').filter(h => h.trim()))} className={textareaClass} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Airport Info Tab */}
                {activeTab === 'airport' && (
                    <div className="max-w-4xl mx-auto">
                        <div className={cardClass}>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Airport & Transportation Info</h3>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={destination.airportInfo?.isActive !== false} 
                                        onChange={e => updateField('airportInfo', { ...destination.airportInfo, isActive: e.target.checked })}
                                        className="w-4 h-4 text-primary focus:ring-primary rounded border-gray-300" 
                                    />
                                    <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Show Section</span>
                                </label>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div>
                                    <label className={labelClass}>Airport Name (EN)</label>
                                    <input type="text" value={destination.airportInfo?.name?.en || ''} onChange={e => updateField('airportInfo', { ...destination.airportInfo, name: { ...destination.airportInfo?.name, en: e.target.value } })} className={inputClass} placeholder="e.g. Milas-Bodrum Airport" />
                                </div>
                                <div>
                                    <label className={labelClass}>Airport Name (SV)</label>
                                    <input type="text" value={destination.airportInfo?.name?.sv || ''} onChange={e => updateField('airportInfo', { ...destination.airportInfo, name: { ...destination.airportInfo?.name, sv: e.target.value } })} className={inputClass} placeholder="e.g. Milas-Bodrum Flygplats" />
                                </div>
                                <div>
                                    <label className={labelClass}>Airport Name (DA)</label>
                                    <input type="text" value={destination.airportInfo?.name?.da || ''} onChange={e => updateField('airportInfo', { ...destination.airportInfo, name: { ...destination.airportInfo?.name, da: e.target.value } })} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>IATA Code</label>
                                    <input type="text" value={destination.airportInfo?.code || ''} onChange={e => updateField('airportInfo', { ...destination.airportInfo, code: e.target.value })} className={inputClass} placeholder="e.g. BJV" maxLength={3} style={{ textTransform: 'uppercase' }} />
                                </div>
                            </div>
                            
                            <MultiLangEditor
                                label="Content & Details"
                                value={destination.airportInfo?.content || { en: '', sv: '', da: '' }}
                                onChange={(lang, html) => updateField('airportInfo', { ...destination.airportInfo, content: { ...destination.airportInfo?.content, [lang]: html } })}
                                isDark={isDark}
                                rows={8}
                            />
                        </div>
                    </div>
                )}

                {/* Practical Info Tab */}
                {activeTab === 'practical' && (
                    <div className="max-w-4xl mx-auto">
                        <div className={cardClass}>
                            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Practical Information</h3>
                            <div className="grid gap-6">
                                <div className="grid grid-cols-4 gap-4">
                                    <div>
                                        <label className={labelClass}>Currency Code</label>
                                        <input type="text" value={destination.sections?.practicalInfo?.currency?.code || ''} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, currency: { ...destination.sections?.practicalInfo?.currency, code: e.target.value } })} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Currency Name (EN)</label>
                                        <input type="text" value={getLocalizedText(destination.sections?.practicalInfo?.currency?.name, 'en')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, currency: { ...destination.sections?.practicalInfo?.currency, name: { ...destination.sections?.practicalInfo?.currency?.name, en: e.target.value } } })} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Currency Name (SV)</label>
                                        <input type="text" value={getLocalizedText(destination.sections?.practicalInfo?.currency?.name, 'sv')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, currency: { ...destination.sections?.practicalInfo?.currency, name: { ...destination.sections?.practicalInfo?.currency?.name, sv: e.target.value } } })} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Currency Name (DA)</label>
                                        <input type="text" value={getLocalizedText(destination.sections?.practicalInfo?.currency?.name, 'da')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, currency: { ...destination.sections?.practicalInfo?.currency, name: { ...destination.sections?.practicalInfo?.currency?.name, da: e.target.value } } })} className={inputClass} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className={labelClass}>Language (EN)</label>
                                        <input type="text" value={getLocalizedText(destination.sections?.practicalInfo?.language, 'en')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, language: { ...destination.sections?.practicalInfo?.language, en: e.target.value } })} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Language (SV)</label>
                                        <input type="text" value={getLocalizedText(destination.sections?.practicalInfo?.language, 'sv')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, language: { ...destination.sections?.practicalInfo?.language, sv: e.target.value } })} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Language (DA)</label>
                                        <input type="text" value={getLocalizedText(destination.sections?.practicalInfo?.language, 'da')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, language: { ...destination.sections?.practicalInfo?.language, da: e.target.value } })} className={inputClass} />
                                    </div>
                                    
                                    <div>
                                        <label className={labelClass}>Timezone (EN)</label>
                                        <input type="text" value={getLocalizedText(destination.sections?.practicalInfo?.timezone, 'en')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, timezone: { ...destination.sections?.practicalInfo?.timezone, en: e.target.value } })} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Timezone (SV)</label>
                                        <input type="text" value={getLocalizedText(destination.sections?.practicalInfo?.timezone, 'sv')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, timezone: { ...destination.sections?.practicalInfo?.timezone, sv: e.target.value } })} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Timezone (DA)</label>
                                        <input type="text" value={getLocalizedText(destination.sections?.practicalInfo?.timezone, 'da')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, timezone: { ...destination.sections?.practicalInfo?.timezone, da: e.target.value } })} className={inputClass} />
                                    </div>
                                </div>
                                <div>
                                    <MultiLangEditor
                                        label="Visa Info"
                                        value={destination.sections?.practicalInfo?.visaInfo || { en: '', sv: '', da: '' }}
                                        onChange={(lang, html) => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, visaInfo: { ...destination.sections?.practicalInfo?.visaInfo, [lang]: html } })}
                                        isDark={isDark}
                                        rows={2}
                                    />
                                </div>
                                <div>
                                    <MultiLangEditor
                                        label="Electricity"
                                        value={destination.sections?.practicalInfo?.electricity || { en: '', sv: '', da: '' }}
                                        onChange={(lang, html) => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, electricity: { ...destination.sections?.practicalInfo?.electricity, [lang]: html } })}
                                        isDark={isDark}
                                        rows={2}
                                    />
                                </div>
                                <div>
                                    <MultiLangEditor
                                        label="Health Info"
                                        value={destination.sections?.practicalInfo?.health || { en: '', sv: '', da: '' }}
                                        onChange={(lang, html) => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, health: { ...destination.sections?.practicalInfo?.health, [lang]: html } })}
                                        isDark={isDark}
                                        rows={2}
                                    />
                                </div>
                                <div>
                                    <MultiLangEditor
                                        label="Emergency Info"
                                        value={destination.sections?.practicalInfo?.emergency || { en: '', sv: '', da: '' }}
                                        onChange={(lang, html) => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, emergency: { ...destination.sections?.practicalInfo?.emergency, [lang]: html } })}
                                        isDark={isDark}
                                        rows={2}
                                    />
                                </div>
                                <div>
                                    <MultiLangEditor
                                        label="Transport Info"
                                        value={destination.sections?.practicalInfo?.transport || { en: '', sv: '', da: '' }}
                                        onChange={(lang, html) => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, transport: { ...destination.sections?.practicalInfo?.transport, [lang]: html } })}
                                        isDark={isDark}
                                        rows={2}
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className={labelClass}>Best Time to Visit (EN)</label>
                                        <input type="text" value={getLocalizedText(destination.sections?.practicalInfo?.bestTimeToVisit, 'en')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, bestTimeToVisit: { ...destination.sections?.practicalInfo?.bestTimeToVisit, en: e.target.value } })} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Best Time to Visit (SV)</label>
                                        <input type="text" value={getLocalizedText(destination.sections?.practicalInfo?.bestTimeToVisit, 'sv')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, bestTimeToVisit: { ...destination.sections?.practicalInfo?.bestTimeToVisit, sv: e.target.value } })} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Best Time to Visit (DA)</label>
                                        <input type="text" value={getLocalizedText(destination.sections?.practicalInfo?.bestTimeToVisit, 'da')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, bestTimeToVisit: { ...destination.sections?.practicalInfo?.bestTimeToVisit, da: e.target.value } })} className={inputClass} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className={labelClass}>Tips (EN) - One per line</label>
                                        <textarea rows={4} value={(destination.sections?.practicalInfo?.tips?.en || []).join('\n')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, tips: { ...destination.sections?.practicalInfo?.tips, en: e.target.value.split('\n').filter(t => t.trim()) } })} className={textareaClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Tips (SV) - One per line</label>
                                        <textarea rows={4} value={(destination.sections?.practicalInfo?.tips?.sv || []).join('\n')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, tips: { ...destination.sections?.practicalInfo?.tips, sv: e.target.value.split('\n').filter(t => t.trim()) } })} className={textareaClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Tips (DA) - One per line</label>
                                        <textarea rows={4} value={(destination.sections?.practicalInfo?.tips?.da || []).join('\n')} onChange={e => updateSections('practicalInfo', { ...destination.sections?.practicalInfo, tips: { ...destination.sections?.practicalInfo?.tips, da: e.target.value.split('\n').filter(t => t.trim()) } })} className={textareaClass} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Weather Tab */}
                {activeTab === 'weather' && (
                    <div className="max-w-4xl mx-auto">
                        <div className={cardClass}>
                            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Monthly Weather</h3>
                            <div className="grid grid-cols-4 gap-4">
                                {['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].map(month => (
                                    <div key={month} className={`p-3 rounded-lg ${isDark ? 'bg-slate-600' : 'bg-white border border-slate-200'}`}>
                                        <h4 className={`font-bold text-center mb-2 capitalize ${isDark ? 'text-white' : 'text-slate-900'}`}>{month}</h4>
                                        <div className="space-y-2">
                                            <div>
                                                <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-500'}`}>Temp (°C)</label>
                                                <input type="number" value={destination.weather?.[month]?.temp || ''} onChange={e => updateWeather(month, 'temp', parseInt(e.target.value))} className={`${inputClass} text-sm`} />
                                            </div>
                                            <div>
                                                <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-500'}`}>Water (°C)</label>
                                                <input type="number" value={destination.weather?.[month]?.water || ''} onChange={e => updateWeather(month, 'water', parseInt(e.target.value))} className={`${inputClass} text-sm`} />
                                            </div>
                                            <div>
                                                <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-500'}`}>Condition</label>
                                                <input type="text" value={destination.weather?.[month]?.condition || ''} onChange={e => updateWeather(month, 'condition', e.target.value)} className={`${inputClass} text-sm`} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Search Config Tab — Tour Search Settings & Hotel Whitelist */}
                {activeTab === 'searchConfig' && (
                    <div className="max-w-4xl mx-auto space-y-6">
                        {/* Tour Search Settings */}
                        <div className={cardClass}>
                            <h3 className={`text-lg font-bold mb-1 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                <span className="material-icons text-indigo-500">flight_takeoff</span>
                                Tour Search Settings
                            </h3>
                            <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Configure API search parameters for this destination's landing page</p>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Check-in Date</label>
                                    <input type="date" className={inputClass} value={(destination as any).tourSearchConfig?.checkIn || ''} onChange={e => setDestination(prev => ({ ...prev, tourSearchConfig: { ...(prev as any).tourSearchConfig, checkIn: e.target.value || undefined } } as any))} />
                                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Empty = auto-calculated from future days</p>
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Future Days</label>
                                    <input type="number" min="0" className={inputClass} value={(destination as any).tourSearchConfig?.futureDays ?? 60} onChange={e => { const v = parseInt(e.target.value); setDestination(prev => ({ ...prev, tourSearchConfig: { ...(prev as any).tourSearchConfig, futureDays: isNaN(v) ? 60 : v } } as any)); }} />
                                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Days from now for default check-in</p>
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Nights</label>
                                    <input type="number" className={inputClass} min={1} max={30} value={(destination as any).tourSearchConfig?.nights ?? 7} onChange={e => setDestination(prev => ({ ...prev, tourSearchConfig: { ...(prev as any).tourSearchConfig, nights: parseInt(e.target.value) || 7 } } as any))} />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Adults</label>
                                    <input type="number" className={inputClass} min={1} max={6} value={(destination as any).tourSearchConfig?.adults ?? 2} onChange={e => setDestination(prev => ({ ...prev, tourSearchConfig: { ...(prev as any).tourSearchConfig, adults: parseInt(e.target.value) || 2 } } as any))} />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Departure ID</label>
                                    <input type="text" className={inputClass} value={(destination as any).tourSearchConfig?.departureId ?? '10'} onChange={e => setDestination(prev => ({ ...prev, tourSearchConfig: { ...(prev as any).tourSearchConfig, departureId: e.target.value } } as any))} placeholder="10 = Stockholm" />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Currency</label>
                                    <select className={inputClass} value={(destination as any).tourSearchConfig?.currency ?? 'SEK'} onChange={e => setDestination(prev => ({ ...prev, tourSearchConfig: { ...(prev as any).tourSearchConfig, currency: e.target.value } } as any))}>
                                        <option value="SEK">SEK</option>
                                        <option value="DKK">DKK</option>
                                        <option value="EUR">EUR</option>
                                        <option value="USD">USD</option>
                                        <option value="GBP">GBP</option>
                                        <option value="TRY">TRY</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Nationality</label>
                                    <input type="text" className={inputClass} value={(destination as any).tourSearchConfig?.nationality ?? 'SE'} onChange={e => setDestination(prev => ({ ...prev, tourSearchConfig: { ...(prev as any).tourSearchConfig, nationality: e.target.value.toUpperCase() } } as any))} placeholder="SE" maxLength={2} />
                                </div>
                            </div>
                            {/* Search Button */}
                            <div className="mt-4 flex items-center gap-3">
                                <button onClick={handleTourSearch} disabled={tourSearchLoading}
                                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-bold text-sm hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 shadow-lg">
                                    <span className="material-icons text-sm">{tourSearchLoading ? 'hourglass_empty' : 'search'}</span>
                                    {tourSearchLoading ? 'Searching...' : '🔍 Search Tours'}
                                </button>
                                {tourSearchResults.length > 0 && (
                                    <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                                        {tourSearchResults.length} hotels found
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Tour Search Results */}
                        {tourSearchResults.length > 0 && (
                            <div className={cardClass}>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                        <span className="material-icons text-blue-500">search</span>
                                        Search Results
                                    </h3>
                                    <button onClick={addSelectedHotelsToWhitelist} disabled={selectedTourHotels.size === 0}
                                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-all disabled:opacity-50">
                                        <span className="material-icons text-sm">add</span>
                                        Add {selectedTourHotels.size} Selected to Whitelist
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                                    {tourSearchResults.map((hotel: any) => {
                                        const hId = String(hotel.productId || hotel.id);
                                        const isSelected = selectedTourHotels.has(hId);
                                        return (
                                            <div key={hId} onClick={() => {
                                                const next = new Set(selectedTourHotels);
                                                isSelected ? next.delete(hId) : next.add(hId);
                                                setSelectedTourHotels(next);
                                            }}
                                                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all border ${isSelected
                                                    ? 'border-emerald-500 bg-emerald-500/10 shadow-md'
                                                    : isDark ? 'border-slate-600 bg-slate-700/50 hover:border-slate-500' : 'border-slate-200 bg-white hover:border-slate-400'}`}>
                                                <input type="checkbox" checked={isSelected} readOnly className="w-4 h-4 accent-emerald-500" />
                                                {(hotel.thumbnailUrl || hotel.imageUrl) && (
                                                    <img src={hotel.thumbnailUrl || hotel.imageUrl} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                                        {hotel.hotelName || hotel.name || hotel.productName || `Hotel ${hId}`}
                                                    </p>
                                                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                                        ID: {hId} {hotel.stars ? `⭐ ${hotel.stars}` : ''} {hotel.price ? `• ${hotel.price} ${(destination as any).tourSearchConfig?.currency || 'SEK'}` : ''}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Hotel Whitelist with DnD */}
                        <div className={cardClass}>
                            <h3 className={`text-lg font-bold mb-1 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                <span className="material-icons text-emerald-500">hotel</span>
                                Hotel Whitelist
                                <span className={`text-sm font-normal ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>(drag to reorder)</span>
                            </h3>
                            <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Only show these hotels in search results. Leave empty to show all hotels.</p>
                            
                            {((destination as any).hotelWhitelist || []).length > 0 && (
                                <div className="space-y-2 mb-4">
                                    {((destination as any).hotelWhitelist || []).map((h: any, i: number) => (
                                        <div key={`wl-${i}`}
                                            draggable
                                            onDragStart={() => handleDragStart(i)}
                                            onDragOver={handleDragOver}
                                            onDrop={() => handleDrop(i)}
                                            className={`flex items-center justify-between rounded-xl px-4 py-3 cursor-grab active:cursor-grabbing transition-all ${dragIdx === i ? 'opacity-50 scale-95' : ''} ${isDark ? 'bg-slate-600 hover:bg-slate-550' : 'bg-slate-50 border border-slate-200 hover:bg-slate-100'}`}>
                                            <div className="flex items-center gap-3">
                                                <span className="material-icons text-slate-400 text-sm">drag_indicator</span>
                                                <span className={`font-medium text-sm px-2 py-0.5 rounded ${isDark ? 'bg-slate-500 text-slate-200' : 'bg-slate-200 text-slate-600'}`}>#{i + 1}</span>
                                                <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{h.name || h.hotelId}</span>
                                                <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>ID: {h.hotelId}</span>
                                                {(destination as any).ctaBar?.hotelId === h.hotelId && (destination as any).ctaBar?.enabled && (
                                                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white">★ CTA</span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => setHotelAsCta(h)} title="Set as Call to Action"
                                                    className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${(destination as any).ctaBar?.hotelId === h.hotelId
                                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                                        : isDark ? 'bg-slate-500 text-slate-300 hover:bg-amber-500/20 hover:text-amber-300' : 'bg-slate-200 text-slate-600 hover:bg-amber-50 hover:text-amber-700'}`}>
                                                    <span className="material-icons text-sm">campaign</span>
                                                </button>
                                                <button onClick={() => {
                                                    const updated = [...((destination as any).hotelWhitelist || [])];
                                                    updated.splice(i, 1);
                                                    setDestination(prev => ({ ...prev, hotelWhitelist: updated } as any));
                                                }} className="text-red-500 hover:text-red-700">
                                                    <span className="material-icons text-sm">close</span>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            <div className="flex gap-3">
                                <input type="text" id="destEditorHotelWhitelistInput" className={`${inputClass} flex-1`} placeholder="Enter Hotel ID" onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        const input = e.currentTarget;
                                        const hotelId = input.value.trim();
                                        if (hotelId) {
                                            setDestination(prev => ({ ...prev, hotelWhitelist: [...((prev as any).hotelWhitelist || []), { hotelId, name: hotelId }] } as any));
                                            input.value = '';
                                        }
                                    }
                                }} />
                                <button onClick={() => {
                                    const input = document.getElementById('destEditorHotelWhitelistInput') as HTMLInputElement;
                                    if (input?.value.trim()) {
                                        setDestination(prev => ({ ...prev, hotelWhitelist: [...((prev as any).hotelWhitelist || []), { hotelId: input.value.trim(), name: input.value.trim() }] } as any));
                                        input.value = '';
                                    }
                                }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors flex items-center gap-1">
                                    <span className="material-icons text-sm">add</span> Add Hotel
                                </button>
                            </div>
                        </div>

                        {/* CTA Bar Configuration */}
                        {(destination as any).ctaBar?.enabled && (
                            <div className={`rounded-2xl p-5 border-2 border-dashed ${isDark ? 'border-amber-500/40 bg-amber-500/5' : 'border-amber-300 bg-amber-50'}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                                        <span className="material-icons">campaign</span>
                                        Call to Action Bar
                                    </h3>
                                    <button onClick={removeCta} className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1">
                                        <span className="material-icons text-sm">delete</span> Remove CTA
                                    </button>
                                </div>
                                <p className={`text-sm mb-3 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                    Hotel: <strong>{(destination as any).ctaBar.hotelName}</strong> (ID: {(destination as any).ctaBar.hotelId})
                                </p>
                                {ctaEditing ? (
                                    <div className="space-y-3">
                                        <div>
                                            <label className={labelClass}>CTA Text (English)</label>
                                            <input type="text" value={ctaTextEn} onChange={e => setCtaTextEn(e.target.value)} className={inputClass}
                                                placeholder="e.g. Book your dream holiday at Royal Hotel — Limited offer!" />
                                        </div>
                                        <div>
                                            <label className={labelClass}>CTA Text (Swedish)</label>
                                            <input type="text" value={ctaTextSv} onChange={e => setCtaTextSv(e.target.value)} className={inputClass}
                                                placeholder="e.g. Boka din drömresa till Royal Hotel — Begränsat erbjudande!" />
                                        </div>
                                        <button onClick={saveCtaText} className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 transition-all">
                                            ✓ Save CTA Text
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <p className={`text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                            🇬🇧 {(destination as any).ctaBar.text?.en || '(no text set)'}
                                        </p>
                                        <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                                            🇸🇪 {(destination as any).ctaBar.text?.sv || '(no text set)'}
                                        </p>
                                        <button onClick={() => { setCtaTextEn((destination as any).ctaBar?.text?.en || ''); setCtaTextSv((destination as any).ctaBar?.text?.sv || ''); setCtaEditing(true); }}
                                            className={`mt-2 px-3 py-1.5 rounded-lg text-sm ${isDark ? 'bg-slate-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                                            ✏️ Edit CTA Text
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Blog Tab */}
                {activeTab === 'blog' && (
                    <div className="p-6 space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                <span className="material-icons text-indigo-500">article</span>
                                Blog Posts
                            </h2>
                            <div className="flex items-center gap-3">
                                <select
                                    value={blogCategory}
                                    onChange={e => setBlogCategory(e.target.value)}
                                    className={inputClass + ' w-auto text-sm'}
                                >
                                    <option value="general">General</option>
                                    <option value="beach">Beaches</option>
                                    <option value="culture">Culture & History</option>
                                    <option value="adventure">Adventure</option>
                                    <option value="luxury">Luxury & Spa</option>
                                    <option value="food">Food & Drink</option>
                                    <option value="nightlife">Nightlife</option>
                                </select>
                                <button
                                    onClick={handleBlogGenerate}
                                    disabled={blogGenerating}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50"
                                >
                                    <span className="material-icons text-sm">{blogGenerating ? 'hourglass_empty' : 'auto_awesome'}</span>
                                    {blogGenerating ? 'Generating...' : 'Generate Blog Post'}
                                </button>
                            </div>
                        </div>

                        {/* Custom Topic Input */}
                        <div className="flex items-center gap-3">
                            <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Custom Topic (optional):</label>
                            <input
                                type="text"
                                value={blogCustomTopic}
                                onChange={e => setBlogCustomTopic(e.target.value)}
                                placeholder="e.g. Best sunset spots in Abu Dhabi"
                                className={`flex-1 px-3 py-2 rounded-lg text-sm border ${isDark ? 'bg-slate-700/50 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'}`}
                            />
                        </div>

                        {/* Blog Message */}
                        {blogMsg && (
                            <div className={`px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2 ${blogMsg.type === 'success'
                                ? (isDark ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-green-100 text-green-700')
                                : (isDark ? 'bg-red-500/20 text-red-300 border border-red-500/30' : 'bg-red-100 text-red-700')}`}>
                                <span className="material-icons text-sm">{blogMsg.type === 'success' ? 'check_circle' : 'error'}</span>
                                {blogMsg.text}
                            </div>
                        )}

                        {/* Blog Preview */}
                        {blogPreview && (
                            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-purple-900/10 border-purple-500/30' : 'bg-purple-50 border-purple-200'}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-bold flex items-center gap-2">
                                        <span className="material-icons text-purple-500">auto_awesome</span>
                                        AI-Generated Preview
                                    </h3>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleBlogSave('draft')}
                                            disabled={blogSaving}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-slate-600 text-white hover:bg-slate-500' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'} disabled:opacity-50`}
                                        >
                                            📝 Save Draft
                                        </button>
                                        <button
                                            onClick={() => handleBlogSave('published')}
                                            disabled={blogSaving}
                                            className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 disabled:opacity-50"
                                        >
                                            🚀 Publish
                                        </button>
                                        <button onClick={() => setBlogPreview(null)} className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                            <span className="material-icons text-sm">close</span>
                                        </button>
                                    </div>
                                </div>
                                {blogPreview.coverImage && (
                                    <div className="mb-4 rounded-xl overflow-hidden h-48">
                                        <img src={blogPreview.coverImage} alt="Cover" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <h4 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{blogPreview.title?.en}</h4>
                                <p className={`text-sm italic mb-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{blogPreview.excerpt?.en}</p>
                                <div className={`text-sm leading-relaxed whitespace-pre-line max-h-64 overflow-y-auto ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                                    {blogPreview.content?.en}
                                </div>
                                {blogPreview.tags?.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-4">
                                        {blogPreview.tags.map((tag: string) => (
                                            <span key={tag} className={`px-2 py-0.5 rounded-full text-xs ${isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>{tag}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Existing Blog Posts */}
                        {blogLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <span className="material-icons animate-spin text-4xl text-primary">refresh</span>
                            </div>
                        ) : blogPosts.length === 0 ? (
                            <div className={`text-center py-12 rounded-2xl ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                                <span className="material-icons text-5xl text-slate-400 mb-3">article</span>
                                <p className={`font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>No blog posts yet</p>
                                <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Select a category and click "Generate" to create AI-powered content</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Inline Blog Editor */}
                                {editingBlog && (
                                    <div className={`p-6 rounded-2xl border-2 ${isDark ? 'bg-indigo-900/20 border-indigo-500/40' : 'bg-indigo-50 border-indigo-200'}`}>
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className={`font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                                <span className="material-icons text-indigo-500">edit</span>
                                                Editing: {editingBlog.title?.en || 'Blog Post'}
                                            </h3>
                                            <div className="flex gap-2">
                                                <button onClick={handleBlogUpdate} disabled={blogSaving}
                                                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 disabled:opacity-50">
                                                    {blogSaving ? '⏳ Saving...' : '✓ Save Changes'}
                                                </button>
                                                <button onClick={() => setEditingBlog(null)}
                                                    className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-200'}`}>
                                                    ✕ Cancel
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            {/* Title */}
                                            {['en', 'sv', 'da'].map(lang => (
                                                <div key={`title-${lang}`}>
                                                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Title ({lang.toUpperCase()})</label>
                                                    <input type="text" value={editingBlog.title?.[lang] || ''}
                                                        onChange={e => setEditingBlog({...editingBlog, title: {...editingBlog.title, [lang]: e.target.value}})}
                                                        className={inputClass} />
                                                </div>
                                            ))}
                                            {/* Excerpt */}
                                            {['en', 'sv', 'da'].map(lang => (
                                                <div key={`excerpt-${lang}`}>
                                                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Excerpt ({lang.toUpperCase()})</label>
                                                    <textarea rows={2} value={editingBlog.excerpt?.[lang] || ''}
                                                        onChange={e => setEditingBlog({...editingBlog, excerpt: {...editingBlog.excerpt, [lang]: e.target.value}})}
                                                        className={textareaClass} />
                                                </div>
                                            ))}
                                            {/* Content */}
                                            {['en', 'sv', 'da'].map(lang => (
                                                <div key={`content-${lang}`}>
                                                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Content ({lang.toUpperCase()})</label>
                                                    <textarea rows={8} value={editingBlog.content?.[lang] || ''}
                                                        onChange={e => setEditingBlog({...editingBlog, content: {...editingBlog.content, [lang]: e.target.value}})}
                                                        className={textareaClass} />
                                                </div>
                                            ))}
                                            {/* Cover Image */}
                                            <div>
                                                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Cover Image URL</label>
                                                <input type="text" value={editingBlog.coverImage || ''}
                                                    onChange={e => setEditingBlog({...editingBlog, coverImage: e.target.value})}
                                                    className={inputClass} placeholder="https://..." />
                                            </div>
                                            {/* Category */}
                                            <div>
                                                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Category</label>
                                                <select value={editingBlog.category || 'general'}
                                                    onChange={e => setEditingBlog({...editingBlog, category: e.target.value})}
                                                    className={inputClass}>
                                                    <option value="general">General</option>
                                                    <option value="beach">Beaches</option>
                                                    <option value="culture">Culture & History</option>
                                                    <option value="adventure">Adventure</option>
                                                    <option value="luxury">Luxury & Spa</option>
                                                    <option value="food">Food & Drink</option>
                                                    <option value="nightlife">Nightlife</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Post Cards Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {blogPosts.map((post: any) => (
                                        <div key={post._id} className={`rounded-2xl overflow-hidden border transition-all ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-white border-slate-200 shadow-sm'}`}>
                                            {post.coverImage && (
                                                <div className="h-32">
                                                    <img src={post.coverImage} alt={post.title?.en} className="w-full h-full object-cover" />
                                                </div>
                                            )}
                                            <div className="p-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${post.status === 'published'
                                                        ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300'
                                                        : post.status === 'archived'
                                                            ? 'bg-slate-100 text-slate-600 dark:bg-slate-600 dark:text-slate-300'
                                                            : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
                                                        }`}>
                                                        {post.status}
                                                    </span>
                                                    <span className={`px-2 py-0.5 rounded-full text-xs ${isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-100 text-slate-500'}`}>
                                                        {post.category}
                                                    </span>
                                                    {post.generatedBy === 'ai' && (
                                                        <span className="text-xs text-purple-500">✨ AI</span>
                                                    )}
                                                </div>
                                                <h4 className={`font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{post.title?.en}</h4>
                                                <p className={`text-sm line-clamp-2 mb-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{post.excerpt?.en}</p>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => setEditingBlog({...post})}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isDark ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'} transition-all`}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                    {post.status === 'draft' && (
                                                        <button
                                                            onClick={() => handleBlogPublish(post._id, 'published')}
                                                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-500 text-white hover:bg-green-600 transition-all"
                                                        >
                                                            Publish
                                                        </button>
                                                    )}
                                                    {post.status === 'published' && (
                                                        <button
                                                            onClick={() => handleBlogPublish(post._id, 'draft')}
                                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isDark ? 'bg-slate-600 text-white' : 'bg-slate-200 text-slate-700'}`}
                                                        >
                                                            Unpublish
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleBlogDelete(post._id)}
                                                        className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all"
                                                    >
                                                        <span className="material-icons text-sm">delete</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Sections Ordering & Visibility Tab */}
                {activeTab === 'sections' && (() => {
                    const DEFAULT_SECTIONS = [
                        { sectionId: 'weather', label: 'Weather', icon: 'wb_sunny' },
                        { sectionId: 'airport_info', label: 'Airport & Transport', icon: 'flight' },
                        { sectionId: 'things_to_do', label: 'Things To Do', icon: 'local_activity' },
                        { sectionId: 'attractions', label: 'Attractions', icon: 'place' },
                        { sectionId: 'tour_packages', label: 'Tour Packages', icon: 'flight_takeoff' },
                        { sectionId: 'price_calendar', label: 'Price Calendar', icon: 'calendar_month' },
                        { sectionId: 'history', label: 'History & Culture', icon: 'history_edu' },
                        { sectionId: 'nature', label: 'Nature & Landscape', icon: 'park' },
                        { sectionId: 'itineraries', label: 'Itineraries', icon: 'route' },
                        { sectionId: 'restaurants', label: 'Restaurants', icon: 'restaurant' },
                        { sectionId: 'blog', label: 'Blog Posts', icon: 'article' },
                        { sectionId: 'api_hotels', label: 'Hotels', icon: 'hotel' },
                        { sectionId: 'featured_hotels', label: 'Featured Hotels', icon: 'star' },
                        { sectionId: 'practical_info', label: 'Practical Info', icon: 'help_outline' },
                    ];

                    // Build current config merging defaults with saved
                    const saved = (destination as any).sectionConfig || [];
                    const sections = DEFAULT_SECTIONS.map((def, idx) => {
                        const s = saved.find((sc: any) => sc.sectionId === def.sectionId);
                        return {
                            sectionId: def.sectionId,
                            label: s?.label || def.label,
                            icon: def.icon,
                            visible: s?.visible !== undefined ? s.visible : (def.sectionId === 'price_calendar' ? false : true),
                            order: s?.order !== undefined ? s.order : idx,
                        };
                    }).sort((a: any, b: any) => a.order - b.order);

                    const moveSection = (idx: number, dir: -1 | 1) => {
                        const newSections = [...sections];
                        const target = idx + dir;
                        if (target < 0 || target >= newSections.length) return;
                        [newSections[idx], newSections[target]] = [newSections[target], newSections[idx]];
                        const updated = newSections.map((s, i) => ({
                            sectionId: s.sectionId,
                            label: s.label,
                            visible: s.visible,
                            order: i
                        }));
                        setDestination({ ...destination, sectionConfig: updated } as any);
                    };

                    const toggleVisibility = (sectionId: string) => {
                        const updated = sections.map(s => ({
                            sectionId: s.sectionId,
                            label: s.label,
                            visible: s.sectionId === sectionId ? !s.visible : s.visible,
                            order: s.order
                        }));
                        setDestination({ ...destination, sectionConfig: updated } as any);
                    };

                    return (
                        <div className="max-w-3xl mx-auto space-y-6">
                            <div className={`${cardClass} !p-6`}>
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                            Page Section Order
                                        </h3>
                                        <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                            Reorder and show/hide sections on the destination page
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                            Use Cache
                                        </label>
                                        <button
                                            onClick={() => setDestination({ ...destination, useCacheData: !(destination as any).useCacheData } as any)}
                                            className={`relative w-12 h-6 rounded-full transition-colors ${
                                                (destination as any).useCacheData !== false
                                                    ? 'bg-green-500'
                                                    : isDark ? 'bg-slate-600' : 'bg-gray-300'
                                            }`}
                                        >
                                            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                                                (destination as any).useCacheData !== false ? 'translate-x-6' : 'translate-x-0.5'
                                            }`} />
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    {sections.map((section: any, idx: number) => (
                                        <div
                                            key={section.sectionId}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                                                section.visible
                                                    ? isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-gray-50 hover:bg-gray-100'
                                                    : isDark ? 'bg-slate-800/50 opacity-50' : 'bg-gray-50/50 opacity-50'
                                            }`}
                                        >
                                            {/* Order number */}
                                            <span className={`w-6 text-center text-xs font-mono ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                                {idx + 1}
                                            </span>

                                            {/* Up/Down buttons */}
                                            <div className="flex flex-col gap-0.5">
                                                <button
                                                    onClick={() => moveSection(idx, -1)}
                                                    disabled={idx === 0}
                                                    className={`p-0.5 rounded transition-colors ${
                                                        idx === 0 ? 'opacity-20 cursor-not-allowed' : 'hover:bg-primary/20 text-primary'
                                                    }`}
                                                >
                                                    <span className="material-icons text-sm">keyboard_arrow_up</span>
                                                </button>
                                                <button
                                                    onClick={() => moveSection(idx, 1)}
                                                    disabled={idx === sections.length - 1}
                                                    className={`p-0.5 rounded transition-colors ${
                                                        idx === sections.length - 1 ? 'opacity-20 cursor-not-allowed' : 'hover:bg-primary/20 text-primary'
                                                    }`}
                                                >
                                                    <span className="material-icons text-sm">keyboard_arrow_down</span>
                                                </button>
                                            </div>

                                            {/* Icon */}
                                            <span className={`material-icons text-lg ${
                                                section.visible
                                                    ? 'text-primary'
                                                    : isDark ? 'text-slate-600' : 'text-gray-400'
                                            }`}>
                                                {section.icon}
                                            </span>

                                            {/* Label */}
                                            <span className={`flex-1 font-medium text-sm ${
                                                section.visible
                                                    ? isDark ? 'text-white' : 'text-slate-900'
                                                    : isDark ? 'text-slate-500 line-through' : 'text-gray-400 line-through'
                                            }`}>
                                                {section.label}
                                            </span>

                                            {/* Visibility toggle */}
                                            <button
                                                onClick={() => toggleVisibility(section.sectionId)}
                                                className={`p-1.5 rounded-lg transition-colors ${
                                                    section.visible
                                                        ? 'bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                                                        : 'bg-red-100 text-red-500 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                                                }`}
                                                title={section.visible ? 'Click to hide' : 'Click to show'}
                                            >
                                                <span className="material-icons text-sm">
                                                    {section.visible ? 'visibility' : 'visibility_off'}
                                                </span>
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <p className={`text-xs mt-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                    💡 Hero, Search Widget, and CTA Bar are always shown. Changes apply after saving.
                                </p>
                            </div>

                            {/* Quick Stats */}
                            <div className="grid grid-cols-4 gap-3">
                                {[
                                    { label: 'Things To Do', count: destination.sections?.thingsToDo?.length || 0, tab: 'activities', color: 'text-primary' },
                                    { label: 'Attractions', count: destination.sections?.attractions?.length || 0, tab: 'attractions', color: 'text-amber-500' },
                                    { label: 'Restaurants', count: destination.sections?.restaurants?.length || 0, tab: 'restaurants', color: 'text-rose-500' },
                                    { label: 'Itineraries', count: destination.sections?.itineraries?.length || 0, tab: 'itineraries', color: 'text-indigo-500' },
                                ].map(s => (
                                    <div key={s.tab} className={cardClass}>
                                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{s.label}</p>
                                        <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
                                        <button onClick={() => setActiveTab(s.tab)} className="text-xs text-primary hover:underline">Edit →</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })()}
            </div>

            {/* Image Picker Modal */}
            <ImagePickerModal
                isOpen={imagePickerOpen}
                onClose={() => { setImagePickerOpen(false); setImagePickerTarget(null); }}
                onSelect={(url) => {
                    if (imagePickerTarget === 'main') {
                        updateField('imageUrl', url);
                    } else if (imagePickerTarget === 'hero') {
                        updateLandingPage('heroImage', url);
                    } else if (typeof imagePickerTarget === 'object' && imagePickerTarget !== null) {
                        const { type, index } = imagePickerTarget;
                        if (type === 'activity') updateActivity(index, 'image', url);
                        else if (type === 'attraction') updateAttraction(index, 'image', url);
                        else if (type === 'itinerary') updateItinerary(index, 'image', url);
                    }
                    setImagePickerOpen(false);
                    setImagePickerTarget(null);
                }}
                title={
                    imagePickerTarget === 'hero' ? 'Select Hero Image'
                        : imagePickerTarget === 'main' ? 'Select Main Image'
                            : typeof imagePickerTarget === 'object' && imagePickerTarget !== null
                                ? `Select ${imagePickerTarget.type.charAt(0).toUpperCase() + imagePickerTarget.type.slice(1)} Image`
                                : 'Select Image'
                }
                language={language}
            />
        </div>
    );
};

export default DestinationEditor;
